import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSources, sourcesFor } from './sources_context';
import D3CandlestickChart from './components/D3CandlestickChart';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Design tokens ──────────────────────────────────────────────────────────
const T = {
  bgBase: '#080b11',
  bgHeader: 'rgba(10,13,20,.82)',
  surface1: '#0e131d',
  surface2: '#0c1118',
  surfaceInset: '#0a0e16',
  surfaceRaised: '#0b0f17',
  border1: '#1a2230',
  border2: '#1e2735',
  borderWarm: '#33301d',
  textHi: '#e7ecf3',
  textBright: '#fff',
  textMid: '#c5cdd9',
  textMid2: '#9aa4b2',
  textMuted: '#8b94a3',
  textMuted2: '#7f8a9a',
  textFaint: '#6b7585',
  gold: '#fbbf24',
  goldLight: '#fcd34d',
  goldDark: '#f59e0b',
  green: '#34d399',
  green2: '#46d39c',
  red: '#f87171',
  red2: '#f48b8b',
  indigo: '#818cf8',
  indigoLight: '#a5b4fc',
  blue: '#7fb0ff',
  blue2: '#58a6ff',
  cyan: '#5fd6df',
  orange: '#f0a35e',
  purple: '#c9a3ff',
  hoverRow: '#10161f',
  mono: "'JetBrains Mono', ui-monospace, monospace",
};

// ── SVG chart utilities ────────────────────────────────────────────────────
function Sparkline({ data, color = T.green, w = 80, h = 24 }) {
  data = (data || []).map((p) => (Array.isArray(p) ? p[1] : p));
  if (!data || data.length < 2) return <svg width={w} height={h} />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const xStep = w / (data.length - 1);
  const pts = data.map((v, i) => `${(i * xStep).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`).join(' ');
  const lastX = (data.length - 1) * xStep;
  const lastY = h - ((data[data.length - 1] - min) / range) * h;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', width: '100%', maxWidth: w, height: 'auto' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
      <circle cx={lastX} cy={lastY} r="2" fill={color} />
    </svg>
  );
}

function AreaChart({ data, color = T.gold, w = 350, h = 120, baseline = null, anchors = null }) {
  const [hover, setHover] = useState(null);
  const pts = (data || []).map((p) => (Array.isArray(p) ? { t: p[0], v: p[1] } : { t: null, v: p }));
  if (pts.length < 2) return <svg viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', width: '100%', maxWidth: w, height: 'auto' }} />;
  const vals = pts.map((p) => p.v);
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const padY = 16, padB = 12;
  const X = (i) => i * (w / (pts.length - 1));
  const Y = (v) => padY + (h - padY - padB - 4) * (1 - (v - min) / range);
  const linePath = pts.map((p, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)},${Y(p.v).toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${w},${h - padB} L0,${h - padB} Z`;
  const gid = `ag-${color.replace('#', '')}-${pts.length}`;
  const hasT = pts[0].t != null && pts[pts.length - 1].t != null;
  const multiday = hasT && (pts[pts.length - 1].t - pts[0].t) / 86400000 > 1.2;
  const fmtTip = (t) => { const d = new Date(t); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + d.toTimeString().slice(0, 5); };
  const fmtTick = (t) => { const d = new Date(t); return multiday ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : d.toTimeString().slice(0, 5); };
  let ticks = [], seps = [];
  if (hasT) {
    if (multiday) {
      let prev = new Date(pts[0].t).getDate();
      const bounds = [0];
      pts.forEach((p, i) => { const dd = new Date(p.t).getDate(); if (dd !== prev) { bounds.push(i); prev = dd; } });
      // draw separators/labels only where there is room — sparse days get no label
      let lastSep = -Infinity;
      bounds.slice(1).forEach((i) => { if (X(i) - lastSep >= w / 20) { seps.push(i); lastSep = X(i); } });
      let lastTick = -Infinity;
      bounds.forEach((i) => { if (X(i) - lastTick >= w / 6.5) { ticks.push(i); lastTick = X(i); } });
    } else {
      for (let k = 0; k < 4; k++) ticks.push(Math.round(k * (pts.length - 1) / 3));
    }
  }
  const hp = hover != null ? pts[hover] : null;
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const frac = (e.clientX - r.left) / r.width;
    setHover(Math.max(0, Math.min(pts.length - 1, Math.round(frac * (pts.length - 1)))));
  };
  return (
    <div style={{ position: 'relative', maxWidth: w }}>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', width: '100%', height: 'auto', cursor: 'crosshair' }}
           onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.32" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((p) => (
          <line key={p} x1="0" x2={w} y1={padY + (h - padY - padB - 4) * p} y2={padY + (h - padY - padB - 4) * p} stroke="#141c28" strokeWidth="1" />
        ))}
        {seps.map((i) => (
          <line key={'s' + i} x1={X(i)} x2={X(i)} y1={padY - 8} y2={h - padB} stroke="#1c2534" strokeWidth="1" />
        ))}
        {baseline != null && baseline >= min && baseline <= max && (
          <line x1="0" x2={w} y1={Y(baseline)} y2={Y(baseline)} stroke={color} strokeWidth="1" strokeDasharray="4 3" opacity="0.45" />
        )}
        <path d={areaPath} fill={`url(#${gid})`} />
        <path d={linePath} fill="none" stroke={color} strokeWidth="2" />
        {anchors && anchors.map((a, k) => {
          const at = Array.isArray(a) ? a[0] : null, av = Array.isArray(a) ? a[1] : a;
          let idx = 0;
          if (at != null && pts[0].t != null) {
            let best = Infinity;
            pts.forEach((p, i) => { const d = Math.abs(p.t - at); if (d < best) { best = d; idx = i; } });
          }
          return <circle key={'a' + k} cx={X(idx)} cy={Y(av)} r="3.2" fill="#0e131d" stroke={color} strokeWidth="1.6" />;
        })}
        <circle cx={X(0)} cy={Y(pts[0].v)} r="3.5" fill="#0e131d" stroke={color} strokeWidth="1.5" />
        <circle cx={X(pts.length - 1)} cy={Y(pts[pts.length - 1].v)} r="4.5" fill={color} opacity="0.25" />
        <circle cx={X(pts.length - 1)} cy={Y(pts[pts.length - 1].v)} r="2.5" fill={color} />
        {hasT && ticks.map((i, k) => (
          <text key={'t' + k} x={Math.min(Math.max(X(i), 16), w - 22)} y={h - 1} fontSize="9" fill="#7f8a9a" textAnchor="middle" fontFamily="'JetBrains Mono', monospace">{fmtTick(pts[i].t)}</text>
        ))}
        {hp && (
          <g>
            <line x1={X(hover)} x2={X(hover)} y1={padY - 8} y2={h - padB} stroke="#8b94a3" strokeWidth="1" strokeDasharray="2 2" />
            <circle cx={X(hover)} cy={Y(hp.v)} r="3.5" fill={color} stroke="#0e131d" strokeWidth="1" />
          </g>
        )}
      </svg>
      {hp && (
        <div style={{
          position: 'absolute', top: 0, left: `${(hover / (pts.length - 1)) * 100}%`,
          transform: hover > pts.length / 2 ? 'translateX(calc(-100% - 8px))' : 'translateX(8px)',
          background: '#0b0f17', border: '1px solid #2a3346', borderRadius: 8, padding: '6px 9px',
          pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 5, boxShadow: '0 4px 14px rgba(0,0,0,.5)',
        }}>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5, fontWeight: 700, color }}>${hp.v}</div>
          {baseline != null && (
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: hp.v >= baseline ? '#34d399' : '#f87171' }}>
              {((hp.v / baseline - 1) * 100 >= 0 ? '+' : '') + ((hp.v / baseline - 1) * 100).toFixed(1)}% vs entry
            </div>
          )}
          {hp.t && <div style={{ fontSize: 10, color: '#8b94a3', marginTop: 1 }}>{fmtTip(hp.t)}</div>}
        </div>
      )}
    </div>
  );
}

function fmtDate(s) { try { const p = String(s).split('-').map(Number); return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][p[1]-1] + ' ' + p[2]; } catch (e) { return s; } }

// ── Stable pseudo-random for deterministic visual fillers ──────────────────
function seedFor(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = (h * 16777619) >>> 0; } return h; }
function genSeries(seedStr, up = true, n = 20) {
  const seed = seedFor(seedStr);
  const arr = [];
  let v = 100;
  let r = seed;
  for (let i = 0; i < n; i++) {
    r = (r * 1103515245 + 12345) & 0x7fffffff;
    const noise = ((r / 0x7fffffff) - 0.5) * 4;
    const drift = up ? 0.6 : -0.6;
    v += drift + noise;
    arr.push(v);
  }
  return arr;
}

// ── Signal badge ───────────────────────────────────────────────────────────
const BADGE_PALETTE = {
  blue: ['rgba(88,166,255,.14)', '#7cb7ff', 'rgba(88,166,255,.3)'],
  orange: ['rgba(240,136,62,.14)', '#f0a35e', 'rgba(240,136,62,.3)'],
  cyan: ['rgba(86,212,221,.13)', '#5fd6df', 'rgba(86,212,221,.3)'],
  green: ['rgba(52,211,153,.13)', '#46d39c', 'rgba(52,211,153,.3)'],
  red: ['rgba(248,113,113,.13)', '#f48b8b', 'rgba(248,113,113,.3)'],
  purple: ['rgba(188,140,255,.14)', '#c9a3ff', 'rgba(188,140,255,.3)'],
  yellow: ['rgba(227,179,65,.14)', '#e8c46b', 'rgba(227,179,65,.3)'],
  amber: ['rgba(251,191,36,.14)', '#fbcf5b', 'rgba(251,191,36,.32)'],
  gray: ['rgba(139,148,163,.12)', '#9aa4b2', 'rgba(139,148,163,.25)'],
};
function Badge({ icon, label, hue = 'gray', title = '' }) {
  const [bg, fg, bd] = BADGE_PALETTE[hue] || BADGE_PALETTE.gray;
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '4px 8px', borderRadius: 7, background: bg, color: fg,
        border: `1px solid ${bd}`, fontSize: 11.5, fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontSize: 12 }}>{icon}</span>
      {label}
    </span>
  );
}

// Build badges from a pick row
function badgesFor(p) {
  const out = [];
  const pred = parseFloat(p.pred_up_pct ?? p.pred ?? 0);
  const rsi = p.rsi != null ? parseFloat(p.rsi) : null;
  const pt = p.pt_upside != null ? parseFloat(p.pt_upside) : null;
  const tilt = parseFloat(p.tilt || 0);
  const dte = p.days_to_earnings;
  if (p.has_news === true || p.has_news === 'True' || p.has_news === 'true') out.push({ icon: '📰', label: 'News', hue: 'blue', title: 'GDELT news in 5d' });
  if (pt != null && pt >= 15) out.push({ icon: '📈', label: `PT +${pt.toFixed(0)}%`, hue: 'green', title: 'Analyst price target above current' });
  if (pt != null && pt < -10) out.push({ icon: '📈', label: `PT ${pt.toFixed(0)}%`, hue: 'red', title: 'Analyst PT below current — disagreement' });
  if (rsi != null && rsi < 30) out.push({ icon: '📊', label: `RSI ${Math.round(rsi)}`, hue: 'cyan', title: 'Oversold' });
  if (rsi != null && rsi > 70) out.push({ icon: '📊', label: `RSI ${Math.round(rsi)}`, hue: 'red', title: 'Overbought' });
  if (dte != null && dte > 0 && dte <= 7) out.push({ icon: '📅', label: `${dte}d`, hue: 'amber', title: `Earnings in ${dte} days` });
  if (p.otm_oi_buying === true || p.otm_oi_buying === 'True') out.push({ icon: '🎯', label: 'OTM calls', hue: 'purple', title: 'Aggressive OTM call buying' });
  if (tilt >= 3) out.push({ icon: '🔥', label: 'High tilt', hue: 'amber', title: `${tilt}/4 confirming signals` });
  if (pred >= 30) out.push({ icon: '🔥', label: 'High pred', hue: 'amber', title: 'Predicted +30% upside' });
  return out;
}

// Cross-source awareness: [📊 PreMarket, 🐦 Reddit, 💬 StockTwits, 🔥 Trending]
function CrossSourceIcons({ flags = [false, false, false, true], style = {} }) {
  const items = [
    { icon: '📊', name: 'Pre-Market' },
    { icon: '🐦', name: 'Reddit/WSB' },
    { icon: '💬', name: 'StockTwits' },
    { icon: '🔥', name: 'Trending' },
  ];
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, ...style }}>
      {items.map((it, i) => (
        <span
          key={i}
          title={`${it.name}: ${flags[i] ? 'flagged' : 'not flagged'}`}
          style={{
            fontSize: 13,
            opacity: flags[i] ? 1 : 0.2,
            filter: flags[i] ? 'none' : 'grayscale(1)',
          }}
        >{it.icon}</span>
      ))}
    </div>
  );
}

// Conviction dots (5)
function ConvictionDots({ tilt = 0 }) {
  return (
    <span style={{ display: 'inline-flex', gap: 3 }}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} style={{
          width: 7, height: 7, borderRadius: '50%',
          background: i < tilt ? T.gold : '#2a3346',
        }} />
      ))}
    </span>
  );
}

// ── StatCard ───────────────────────────────────────────────────────────────
function StatCard({ label, icon, value, unit, sub, color = T.textHi, bd = T.border2 }) {
  return (
    <div style={{
      background: T.surface1, border: `1px solid ${bd}`, borderRadius: 16,
      padding: 18, display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{
          fontSize: 11, fontWeight: 700, color: T.textMuted2,
          letterSpacing: '.06em', textTransform: 'uppercase',
        }}>{label}</span>
        <span style={{ fontSize: 15 }}>{icon}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontFamily: T.mono, fontSize: 27, fontWeight: 700, color, letterSpacing: '-.02em' }}>{value}</span>
        {unit && <span style={{ color: T.textMuted2, fontSize: 14, fontWeight: 600 }}>{unit}</span>}
      </div>
      <span style={{ color: T.textMuted2, fontSize: 11.5 }}>{sub}</span>
    </div>
  );
}

// ── Hero StatCard (the big one) ────────────────────────────────────────────
function HeroStatCard({ value, unit, label, sub, edgeText, backtestText, positiveText }) {
  return (
    <div style={{
      background: 'linear-gradient(155deg,#101826,#0c111b)',
      border: `1px solid ${T.border2}`, borderRadius: 18, padding: 24,
      gridRow: 'span 2', display: 'flex', flexDirection: 'column', gap: 14,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', right: -30, top: -10, fontSize: 130,
        opacity: 0.07, transform: 'rotate(-12deg)', pointerEvents: 'none',
      }}>🎯</div>
      <div style={{
        fontSize: 11.5, fontWeight: 700, color: T.textMuted2,
        letterSpacing: '.06em', textTransform: 'uppercase',
      }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontFamily: T.mono, fontSize: 56, fontWeight: 700, color: T.textBright, letterSpacing: '-.03em', lineHeight: 1 }}>{value}</span>
        <span style={{ fontSize: 22, color: T.textMid, fontWeight: 600 }}>{unit}</span>
      </div>
      <div style={{ color: T.textMid2, fontSize: 13 }}>{sub}</div>
      <div style={{
        display: 'inline-flex', alignSelf: 'flex-start', padding: '5px 11px',
        borderRadius: 8, background: 'rgba(52,211,153,.12)', border: '1px solid rgba(52,211,153,.32)',
        color: T.green, fontSize: 12, fontWeight: 700,
      }}>▲ {edgeText}</div>
      <div style={{ display: 'flex', gap: 24, marginTop: 'auto' }}>
        <div>
          <div style={{ fontSize: 10, color: T.textMuted2, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase' }}>Backtest</div>
          <div style={{ fontFamily: T.mono, fontSize: 17, fontWeight: 700, color: T.textHi, marginTop: 4 }}>{backtestText}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: T.textMuted2, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase' }}>Positive wks</div>
          <div style={{ fontFamily: T.mono, fontSize: 17, fontWeight: 700, color: T.green, marginTop: 4 }}>{positiveText}</div>
        </div>
      </div>
    </div>
  );
}

// ── PickCard (Standard variant) ────────────────────────────────────────────
function PickCard({ pick, rank, highConv = false, style, onOpen, todayView = false, vol }) {
  const side = pick.side || 'call';
  const [symHover, setSymHover] = useState(false);
  const isCall = side === 'call';
  const sources = useSources();
  const cross = sourcesFor(sources, pick.ticker);
  const badges = badgesFor(pick);

  const pnl = pick.opt_pnl_pct;
  const hasPnl = pnl !== undefined && pnl !== null;
  const reason = pick.opt_exit_reason;
  const open = pick.opt_open || reason === 'end_of_window';
  const pnlPos = hasPnl && pnl >= 0;
  const pnlColor = pnlPos ? T.green : T.red;

  // option price path (entry -> latest/exit) — the P&L chart
  const optSeries = (pick.opt_series && pick.opt_series.length >= 2) ? pick.opt_series : [];
  // stock move since entry (or pre-entry run-up for pending live picks)
  const stk = useMemo(() => {
    const real = pick.prices_fwd14 || pick.prices_back20;
    return (real && real.length >= 2) ? real : [];
  }, [pick.prices_fwd14, pick.prices_back20]);
  const isForward = !!pick.prices_fwd14;
  const stockCh = stk.length >= 2 ? ((stk[stk.length - 1] - stk[0]) / stk[0] * 100) : 0;
  const helps = isForward && (isCall ? stockCh > 0 : stockCh < 0);

  // chart: prefer the option price curve; fall back to the stock setup chart
  const chart = optSeries.length ? optSeries : stk;
  const chartColor = optSeries.length ? pnlColor : (isForward ? (helps ? T.green : T.red) : T.textMuted2);

  let badgeText = 'CLOSED', badgeColor = T.textMid;
  if (pick.opt_status === 'pending') { badgeText = '🟡 JUST OPENED'; badgeColor = T.indigoLight; }
  else if (open) { badgeText = todayView ? '● LIVE' : '🟢 HOLDING'; badgeColor = pnlPos ? T.green : T.red; }
  else if (reason === 'profit_target') { badgeText = '✅ WON · TARGET'; badgeColor = T.green; }
  else if (reason === 'stop_loss') { badgeText = '🔴 LOST · STOPPED'; badgeColor = T.red; }
  else if (reason === 'time_stop') { badgeText = (pnlPos ? '✅ WON' : '🔴 LOST') + ' · 7-DAY'; badgeColor = pnlPos ? T.green : T.red; }

  const conf = isCall
    ? `model target +${parseFloat(pick.pred_up_pct || 0).toFixed(0)}%`
    : `model P(put) ${Math.round(parseFloat(pick.p_put || 0) * 100)}%`;
  const accent = isCall ? T.green : T.red;

  return (
    <div onClick={() => onOpen && onOpen('chart')} title="Click chart · ticker for details · 📰 for news" style={{
      background: highConv ? 'linear-gradient(160deg,#13110a,#0d1119)' : T.surface1,
      border: `1px solid ${highConv ? T.borderWarm : T.border2}`,
      borderRadius: 16, padding: 16, cursor: onOpen ? 'pointer' : 'default',
      boxShadow: highConv ? '0 0 0 1px rgba(251,191,36,.25), 0 0 26px -8px rgba(251,191,36,.3)' : 'none',
      ...style,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontFamily: T.mono, fontSize: 12, fontWeight: 700, padding: '3px 8px', borderRadius: 6, background: highConv ? 'linear-gradient(180deg,#3a2f12,#2a2009)' : '#1a2230', color: highConv ? T.gold : T.textMuted }}>#{rank}</span>
        <span onClick={(e) => { e.stopPropagation(); onOpen && onOpen('details'); }} onMouseEnter={() => setSymHover(true)} onMouseLeave={() => setSymHover(false)} title="Trade details · info panel" style={{ fontFamily: T.mono, fontSize: 21, fontWeight: 700, color: T.textBright, cursor: 'pointer', textDecoration: symHover ? 'underline' : 'none', textUnderlineOffset: 3 }}>{pick.ticker}</span>
        <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 10.5, fontWeight: 700, background: isCall ? 'rgba(52,211,153,.13)' : 'rgba(248,113,113,.13)', color: accent, border: `1px solid ${isCall ? 'rgba(52,211,153,.3)' : 'rgba(248,113,113,.3)'}` }}>{isCall ? 'CALL · profits if ↑' : 'PUT · profits if ↓'}</span>
        <button onClick={(e) => { e.stopPropagation(); onOpen && onOpen('news'); }} title="News" style={{ marginLeft: 'auto', background: T.surface2, border: `1px solid ${T.border2}`, borderRadius: 7, padding: '2px 8px', fontSize: 12, cursor: 'pointer' }}>📰</button>
        <CrossSourceIcons flags={cross} style={{ marginLeft: 8 }} />
      </div>

      {/* P&L hero + option chart */}
      {hasPnl ? (
        <div style={{ borderRadius: 12, padding: '12px 13px', marginBottom: 11, background: pnlPos ? 'rgba(52,211,153,.09)' : 'rgba(248,113,113,.09)', border: `1px solid ${pnlPos ? 'rgba(52,211,153,.28)' : 'rgba(248,113,113,.28)'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontFamily: T.mono, fontSize: 28, fontWeight: 800, color: pnlColor, letterSpacing: '-.02em' }}>{(pnlPos ? '+' : '') + pnl.toFixed(0)}%</span>
                <span style={{ fontSize: 11, color: T.textMuted2 }}>{todayView ? 'if bought at first trade today' : 'option P&L'}{pick.opt_modeled ? ' · est (modeled)' : ''}{open ? (pick.opt_live_asof ? ' · live ' + String(pick.opt_live_asof).slice(11, 16) : ' (live)') : ''}</span>
              </div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>
                {todayView ? '' : `opened ${fmtDate(pick.date)}`}{!todayView && pick.opt_days_held != null ? ` · ${open ? 'day' : 'closed day'} ${pick.opt_days_held}` : ''}{pick.opt_entry_price ? `${todayView ? '' : ' · '}entry $${pick.opt_entry_price}` : ''}{pick.opt_strike ? ` · $${pick.opt_strike} ${side} exp ${fmtDate(pick.opt_expiry)}` : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '.03em', padding: '4px 9px', borderRadius: 7, color: badgeColor, background: badgeColor + '1f', border: `1px solid ${badgeColor}44`, whiteSpace: 'nowrap' }}>{badgeText}</span>
              {optSeries.length >= 2 && (
                <div style={{ marginTop: 8 }}>
                  <Sparkline data={optSeries} color={pnlColor} w={104} h={32} />
                  <div style={{ fontSize: 9.5, color: T.textFaint, fontFamily: T.mono, marginTop: 1 }}>${Array.isArray(optSeries[0]) ? optSeries[0][1] : optSeries[0]} → ${Array.isArray(optSeries[optSeries.length - 1]) ? optSeries[optSeries.length - 1][1] : optSeries[optSeries.length - 1]}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ borderRadius: 12, padding: '12px 13px', marginBottom: 11, background: 'rgba(129,140,248,.08)', border: '1px solid rgba(129,140,248,.22)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13.5, color: T.indigoLight, fontWeight: 700 }}>{todayView ? '🟡 No option trades yet today' : `🟡 Opened ${fmtDate(pick.date)}`}</span>
          <span style={{ fontSize: 10.5, color: T.textMuted2 }}>P&L starts after next close</span>
        </div>
      )}

      {/* stock move (side-aware) + minimal confidence + small chart fallback */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: badges.length ? 10 : 0 }}>
        <div style={{ fontSize: 11, fontFamily: T.mono, color: stk.length >= 2 ? (isForward ? (helps ? T.green : T.red) : T.textMuted2) : T.textFaint }}>
          {stk.length >= 2
            ? (isForward
                ? `stock ${stockCh >= 0 ? '+' : ''}${stockCh.toFixed(1)}% ${helps ? '✓ your way' : '✗ against'}`
                : `stock ${stockCh >= 0 ? '+' : ''}${stockCh.toFixed(1)}% in 20d before pick`)
            : ''}
        </div>
        {vol ? <div style={{ fontSize: 11, fontFamily: T.mono, color: T.textMuted, whiteSpace: 'nowrap' }}>vol {vol >= 1e6 ? (vol / 1e6).toFixed(1) + 'M' : Math.round(vol / 1e3) + 'K'}</div> : null}
      </div>
      {(pick.pred_up_pct != null || pick.p_put != null) && (
        <div style={{ marginTop: 8, marginBottom: badges.length ? 8 : 0, padding: '7px 10px', borderRadius: 9, background: 'rgba(129,140,248,.06)', border: '1px solid rgba(129,140,248,.16)', fontFamily: T.mono, fontSize: 11, color: T.indigoLight, lineHeight: 1.5 }}>
          🧠 {todayView ? '' : 'at pick · '}{isCall
            ? `pred +${parseFloat(pick.pred_up_pct || 0).toFixed(0)}% (10d max) · score ${parseFloat(pick.tilted_score || 0).toFixed(0)} · tilt ${parseInt(pick.tilt || 0, 10)}/4${pick.ml_hit15_pct != null ? ` · similar picks hit ≥15%: ${pick.ml_hit15_pct}%` : ''}`
            : `P(put) ${Math.round(parseFloat(pick.p_put || 0) * 100)}% classifier confidence`}
        </div>
      )}

      {/* signal badges (restored for color) */}
      {badges.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {badges.map((b, i) => <Badge key={i} icon={b.icon} label={b.label} hue={b.hue} title={b.title} />)}
        </div>
      )}
    </div>
  );
}

// ── Hero pick (the dominant card) ──────────────────────────────────────────
function HeroPick({ pick, aggregate, held, live, livePut, liveAsOf }) {
  const heroSources = useSources();
  const heroFlags = sourcesFor(heroSources, (pick && pick.ticker) ? pick.ticker : "NA");
  const safeTicker = (pick && pick.ticker) ? pick.ticker : 'NA';
  const series = useMemo(() => {
    const real = (pick && (pick.prices_back20 || pick.prices_fwd14)) || [];
    return real.length >= 2 ? real : [];
  }, [pick]);
  const hasSeries = series.length >= 2;
  if (!pick) return null;
  const pred = parseFloat(pick.pred_up_pct || 0);
  const tilt = parseInt(pick.tilt || 0, 10);
  const score = parseFloat(pick.tilted_score || pred);
  const badges = badgesFor(pick);
  const ch = hasSeries ? ((series[series.length - 1] - series[0]) / series[0] * 100) : 0;
  const monsterRate = aggregate?.hit_rate_30pct ?? 0;
  const monsterOdds = monsterRate > 0 ? `1 in ${Math.max(2, Math.round(100 / monsterRate))}` : '1 in many';
  return (
    <div style={{
      background: 'linear-gradient(160deg,#13110a,#0d1119)',
      border: `1px solid ${T.borderWarm}`,
      borderRadius: 20, padding: 24, marginTop: 24,
      boxShadow: '0 0 0 1px rgba(251,191,36,.45), 0 0 30px -6px rgba(251,191,36,.28)',
      animation: 'hglow 4.5s ease-in-out infinite',
      position: 'relative',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800, color: T.gold, letterSpacing: '.1em', textTransform: 'uppercase' }}>🐲 Today's #1 Call · locked at open</div>
        {live && !live.pending && liveAsOf ? <span style={{ fontSize: 11, color: T.textMuted2, fontFamily: T.mono }}>live P&L as of {String(liveAsOf).slice(11, 16)}</span> : <span style={{ fontSize: 11, color: T.textMuted2, fontFamily: T.mono }}>{liveAsOf ? 'updated ' + String(liveAsOf).slice(11, 16) + ' · awaiting first option trade' : 'P&L resumes at next open'}</span>}
      </div>
      {held && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 0 14px', padding: '9px 12px', borderRadius: 10, background: 'rgba(248,113,113,.08)', border: '1px solid rgba(248,113,113,.28)' }}>
          <span style={{ fontSize: 12, color: T.red2, fontWeight: 700 }}>⚠️ You already hold this since {fmtDate(held.date)}</span>
          <span style={{ fontFamily: T.mono, fontSize: 12, fontWeight: 800, color: (held.opt_pnl_pct >= 0 ? T.green : T.red) }}>{(held.opt_pnl_pct >= 0 ? '+' : '') + Number(held.opt_pnl_pct).toFixed(0)}%</span>
          <span style={{ fontSize: 11, color: T.textMuted2 }}>— the model re-picks it daily; not a fresh setup.</span>
        </div>
      )}
      <div className="g-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28, alignItems: 'center' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <span style={{ fontFamily: T.mono, fontSize: 40, fontWeight: 700, color: T.textBright, letterSpacing: '-.02em' }}>{pick.ticker}</span>
            <span style={{
              padding: '4px 12px', borderRadius: 8, fontSize: 13, fontWeight: 700,
              background: 'rgba(52,211,153,.15)', color: T.green,
              border: '1px solid rgba(52,211,153,.32)',
            }}>CALL</span>
            {live && live.strike && (
              <span style={{ fontFamily: T.mono, fontSize: 13, color: T.textMid, padding: '4px 10px', borderRadius: 8, background: T.surface2, border: `1px solid ${T.border2}` }}>
                ${live.strike} strike · exp {fmtDate(live.expiry)}
              </span>
            )}
            <CrossSourceIcons flags={heroFlags} />
          </div>
          {live && !live.pending ? (() => {
            const lp = Number(live.pnl_pct);
            const pos = lp >= 0;
            return (
              <div style={{ borderRadius: 12, padding: '12px 14px', marginBottom: 14, background: pos ? 'rgba(52,211,153,.09)' : 'rgba(248,113,113,.09)', border: `1px solid ${pos ? 'rgba(52,211,153,.3)' : 'rgba(248,113,113,.3)'}` }}>
                <div style={{ fontSize: 11, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>If bought at today's open · ATM option · live</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontFamily: T.mono, fontSize: 36, fontWeight: 800, color: pos ? T.green : T.red, letterSpacing: '-.02em' }}>{(pos ? '+' : '') + lp.toFixed(1)}%</span>
                  <span style={{ fontSize: 12.5, color: T.textMuted2, fontFamily: T.mono }}>${live.entry} → ${live.current}</span>
                </div>
              </div>
            );
          })() : (
            <div style={{ borderRadius: 12, padding: '12px 14px', marginBottom: 14, background: 'rgba(129,140,248,.07)', border: '1px solid rgba(129,140,248,.2)', fontSize: 12.5, color: T.indigoLight, fontWeight: 600 }}>
              🟡 Option hasn't printed a trade yet today — stock shown live; P&L appears on its first trade
            </div>
          )}
          <div style={{ display: 'flex', gap: 20, marginBottom: 14, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: T.textMuted2 }}>model target <span style={{ fontFamily: T.mono, fontWeight: 700, color: T.textMid }}>+{pred.toFixed(0)}%</span></span>
            <span style={{ fontSize: 12, color: T.textMuted2 }}>score <span style={{ fontFamily: T.mono, fontWeight: 700, color: T.textMid }}>{score.toFixed(0)}</span></span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.textMuted2 }}>tilt <ConvictionDots tilt={tilt} /></span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
            {badges.map((b, i) => <Badge key={i} icon={b.icon} label={b.label} hue={b.hue} title={b.title} />)}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={{
              padding: '10px 16px', borderRadius: 10,
              background: 'linear-gradient(180deg,#221c11,#181208)',
              border: `1px solid ${T.borderWarm}`,
              color: T.gold, fontSize: 13, fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 6px 18px -6px rgba(245,158,11,.5)', fontFamily: 'inherit',
            }}>Trade this setup →</button>
            <button style={{
              padding: '10px 16px', borderRadius: 10, background: T.surface2,
              border: `1px solid ${T.border2}`, color: T.textMid, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>View option chain</button>
          </div>
          {livePut && !livePut.pending && (
            <div style={{ marginTop: 14, fontSize: 12, color: T.textMuted2 }}>
              Today's #1 PUT: <span style={{ fontFamily: T.mono, fontWeight: 700, color: T.textHi }}>{livePut.ticker}</span>{livePut.strike ? ` $${livePut.strike} exp ${fmtDate(livePut.expiry)}` : ''}{' '}
              <span style={{ fontFamily: T.mono, fontWeight: 800, color: Number(livePut.pnl_pct) >= 0 ? T.green : T.red }}>{(Number(livePut.pnl_pct) >= 0 ? '+' : '') + Number(livePut.pnl_pct).toFixed(1)}%</span> live since open
            </div>
          )}
        </div>
        <div>
          {(() => {
            const liveS = (live && !live.pending && live.series && live.series.length >= 2) ? live.series : null;
            const stkS = (live && live.stock_series && live.stock_series.length >= 2) ? live.stock_series : null;
            const lp = liveS ? Number(live.pnl_pct) : 0;
            const sc = stkS ? Number(live.stock_change_pct || 0) : 0;
            const cData = liveS || stkS || series;
            const cColor = liveS ? (lp >= 0 ? T.green : T.red) : stkS ? (sc >= 0 ? T.green : T.red) : T.gold;
            const label = liveS ? 'Option price today · live' : stkS ? 'Stock price today · live' : 'Stock · 20 days before entry (setup, not P&L)';
            const value = liveS ? `${lp >= 0 ? '+' : ''}${lp.toFixed(1)}% since open`
              : stkS ? `stock ${sc >= 0 ? '+' : ''}${sc.toFixed(1)}% today`
              : (hasSeries ? `stock ${ch >= 0 ? '+' : ''}${ch.toFixed(1)}% in 20d before pick` : 'no chart data');
            return (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                  <span style={{ fontSize: 11, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>{label}</span>
                  <span style={{ fontFamily: T.mono, fontSize: 13, color: cColor, fontWeight: 600 }}>{value}</span>
                </div>
                <AreaChart data={cData} color={cColor} w={400} h={130} />
              </>
            );
          })()}
          <div style={{
            marginTop: 14, padding: 14, borderRadius: 12,
            background: 'rgba(251,191,36,.04)', border: '1px solid rgba(251,191,36,.15)',
          }}>
            <div style={{ fontSize: 10.5, color: T.gold, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 }}>
              Historical rates at this conviction
            </div>
            <div style={{ fontSize: 13, color: T.textMid }}>
              ~{monsterRate.toFixed(0)}% chance of a &gt;30% move; ~{monsterOdds} becomes a monster.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Best open position (the winner you're holding) ────────────────────────
function BestOpenCard({ pick }) {
  const sources = useSources();
  const cross = sourcesFor(sources, pick.ticker);
  const isCall = (pick.side || 'call') === 'call';
  const pnl = pick.opt_pnl_pct;
  const pos = pnl >= 0;
  const pnlColor = pos ? T.green : T.red;
  const optS = (pick.opt_series && pick.opt_series.length >= 2) ? pick.opt_series : [];
  const reason = pick.opt_exit_reason;
  const open = pick.opt_open || reason === 'end_of_window';
  const badgeText = open ? '🟢 HOLDING' : reason === 'profit_target' ? '✅ WON · TARGET' : reason === 'stop_loss' ? '🔴 LOST' : '✅ CLOSED';
  const accent = isCall ? T.green : T.red;
  return (
    <div style={{ background: 'linear-gradient(160deg,#0a1410,#0b1016)', border: `1px solid ${pos ? 'rgba(52,211,153,.32)' : 'rgba(248,113,113,.32)'}`, borderRadius: 20, padding: 22, marginTop: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: pnlColor, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 14 }}>🏆 Best Open Position</div>
      <div className="g-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28, alignItems: 'center' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <span style={{ fontFamily: T.mono, fontSize: 34, fontWeight: 700, color: T.textBright }}>{pick.ticker}</span>
            <span style={{ padding: '4px 11px', borderRadius: 8, fontSize: 12, fontWeight: 700, background: isCall ? 'rgba(52,211,153,.14)' : 'rgba(248,113,113,.14)', color: accent, border: `1px solid ${isCall ? 'rgba(52,211,153,.3)' : 'rgba(248,113,113,.3)'}` }}>{isCall ? 'CALL · profits if ↑' : 'PUT · profits if ↓'}</span>
            <span style={{ padding: '4px 10px', borderRadius: 7, fontSize: 11, fontWeight: 800, color: pnlColor, background: pnlColor + '1f', border: `1px solid ${pnlColor}44` }}>{badgeText}</span>
            <CrossSourceIcons flags={cross} style={{ marginLeft: 'auto' }} />
          </div>
          <div style={{ fontFamily: T.mono, fontSize: 44, fontWeight: 800, color: pnlColor, letterSpacing: '-.02em' }}>{(pos ? '+' : '') + Number(pnl).toFixed(0)}%</div>
          <div style={{ fontSize: 12, color: T.textMuted2, marginTop: 2 }}>option P&L{pick.opt_modeled ? ' · est (modeled)' : ''}{open ? (pick.opt_live_asof ? ' · live ' + String(pick.opt_live_asof).slice(11, 16) : ' (live)') : ''}</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>opened {fmtDate(pick.date)}{pick.opt_days_held != null ? ` · day ${pick.opt_days_held}` : ''}</div>
          {optS.length >= 2 && (() => {
            const ov = optS.map((p) => (Array.isArray(p) ? p[1] : p));
            const hi = Math.max(...ov), lo = Math.min(...ov), last = ov[ov.length - 1];
            const ent = pick.opt_entry_price || ov[0];
            return (
              <div style={{ fontFamily: T.mono, fontSize: 13, color: T.textMid, marginTop: 10, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <span>entry <b style={{ color: T.textHi }}>${ent}</b></span>
                <span>now <b style={{ color: pnlColor }}>${last}</b></span>
                <span style={{ color: T.textMuted2 }}>high ${hi} · low ${lo}</span>
              </div>
            );
          })()}
        </div>
        <div>
          {optS.length >= 2
            ? <AreaChart data={optS} color={pnlColor} w={400} h={120} baseline={pick.opt_entry_price || (Array.isArray(optS[0]) ? optS[0][1] : optS[0])} anchors={pick.opt_anchors} />
            : <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textFaint, fontSize: 12 }}>chart building…</div>}
          <div style={{ fontSize: 10.5, color: T.textFaint, fontFamily: T.mono, textAlign: 'right', marginTop: 4 }}>{optS.length >= 2 ? (pick.opt_modeled ? `value curve modeled from the stock's 5-min moves · rings = ${(pick.opt_anchors || []).length} real option trades · dashed = entry` : `option price · 1-min trades since ${fmtDate(pick.date)} · dashed = entry`) : ''}</div>
        </div>
      </div>
    </div>
  );
}

// ── Week picker bars ───────────────────────────────────────────────────────
function WeekPicker({ weekly, selected, onSelect }) {
  if (!weekly || weekly.length === 0) return null;
  const maxHit = Math.max(...weekly.map((w) => (w.call_15pct_hits / Math.max(1, w.call_n)) * 100));
  return (
    <div style={{
      background: T.surfaceRaised, border: `1px solid ${T.border1}`,
      borderRadius: 16, padding: 18, marginTop: 28,
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: T.textMuted2,
        letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 14,
      }}>📅 Historical weeks — select to review outcomes</div>
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6 }}>
        {weekly.map((w) => {
          const hit = (w.call_15pct_hits / Math.max(1, w.call_n)) * 100;
          const isSel = selected === w.week;
          let barColor = T.orange;
          if (hit >= 60) barColor = T.green;
          else if (hit >= 45) barColor = '#5e9e7e';
          else if (hit >= 35) barColor = T.gold;
          const barH = Math.max(20, (hit / maxHit) * 120);
          return (
            <div
              key={w.week}
              onClick={() => onSelect(w.week)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                cursor: 'pointer', minWidth: 56,
              }}
            >
              <div style={{ fontSize: 10, color: isSel ? T.gold : T.textMuted2, fontFamily: T.mono, fontWeight: 600 }}>
                {hit.toFixed(0)}%
              </div>
              <div style={{
                width: 28, height: barH,
                background: isSel
                  ? 'linear-gradient(180deg,#fcd34d,#f59e0b)'
                  : barColor,
                borderRadius: 6,
                boxShadow: isSel
                  ? '0 0 0 1px rgba(251,191,36,.45), 0 0 18px -2px rgba(251,191,36,.5)'
                  : 'none',
                transition: 'all .15s',
              }} />
              <div style={{
                fontSize: 10.5, color: isSel ? T.gold : T.textMuted, fontWeight: isSel ? 700 : 500,
                fontFamily: T.mono,
              }}>{w.week.replace('2026-', '')}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Week detail summary banner ─────────────────────────────────────────────
function WeekDetail({ week, side, onSideChange, sort, onSortChange }) {
  if (!week) return null;
  const hit15 = ((week.call_15pct_hits / Math.max(1, week.call_n)) * 100).toFixed(0);
  const monster = ((week.call_monster_hits / Math.max(1, week.call_n)) * 100).toFixed(0);
  return (
    <div style={{
      background: 'linear-gradient(155deg,rgba(251,191,36,.06),rgba(245,158,11,.02))',
      border: `1px solid ${T.borderWarm}`, borderRadius: 16, padding: 20, marginTop: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: T.textHi }}>
            Week {week.week.replace('2026-W', '')} · {week.call_n} picks
          </h3>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ display: 'inline-flex', padding: 3, borderRadius: 10, background: T.surface2, border: `1px solid ${T.border2}` }}>
            {['calls', 'puts'].map((s) => (
              <button
                key={s}
                onClick={() => onSideChange(s)}
                style={{
                  padding: '6px 12px', borderRadius: 7, border: 'none',
                  background: side === s ? '#1d2735' : 'transparent',
                  color: side === s ? T.textHi : T.textMuted2,
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                {s === 'calls' ? `Calls (${week.call_n})` : `Puts (${week.put_n || 0})`}
              </button>
            ))}
          </div>
          <select value={sort} onChange={(e) => onSortChange(e.target.value)} style={{
            padding: '7px 11px', borderRadius: 9, background: T.surface2, border: `1px solid ${T.border2}`,
            color: T.textMid, fontSize: 12, fontFamily: 'inherit',
          }}>
            <option value="actual">Sort: Actual realized return</option>
            <option value="pred">Sort: Predicted</option>
            <option value="pnl">Sort: Option P&L est.</option>
          </select>
        </div>
      </div>
      <div className="g-multi" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
        <div>
          <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>15%+ hit</div>
          <div style={{ fontFamily: T.mono, fontSize: 20, fontWeight: 700, color: T.green, marginTop: 4 }}>{hit15}%</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>Monsters %</div>
          <div style={{ fontFamily: T.mono, fontSize: 20, fontWeight: 700, color: T.gold, marginTop: 4 }}>{monster}%</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>Avg pred → act</div>
          <div style={{ fontFamily: T.mono, fontSize: 16, fontWeight: 700, color: T.textHi, marginTop: 4 }}>
            +{week.avg_call_pred?.toFixed(0) ?? '-'}% → <span style={{ color: T.green }}>+{week.avg_call_actual?.toFixed(0) ?? '-'}%</span>
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>Best ▲</div>
          <div style={{ fontFamily: T.mono, fontSize: 14, fontWeight: 700, color: T.green, marginTop: 4 }}>
            {week.best_pick?.ticker ?? '—'} {week.best_pick ? `+${week.best_pick.fwd.toFixed(0)}%` : ''}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>Worst ▼</div>
          <div style={{ fontFamily: T.mono, fontSize: 14, fontWeight: 700, color: T.red, marginTop: 4 }}>
            {week.worst_pick?.ticker ?? '—'} {week.worst_pick ? `${week.worst_pick.fwd >= 0 ? '+' : ''}${week.worst_pick.fwd.toFixed(0)}%` : ''}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Week outcome table ─────────────────────────────────────────────────────
function WeekOutcomeTable({ picks, side, sort }) {
  const sorted = useMemo(() => {
    if (!picks) return [];
    const arr = [...picks];
    arr.sort((a, b) => {
      const av = sort === 'pred' ? parseFloat(a.pred_up_pct || 0) : sort === 'pnl' ? parseFloat(a.option_pnl_est_pct || 0) : parseFloat(a.out_max_up_7d || 0);
      const bv = sort === 'pred' ? parseFloat(b.pred_up_pct || 0) : sort === 'pnl' ? parseFloat(b.option_pnl_est_pct || 0) : parseFloat(b.out_max_up_7d || 0);
      return bv - av;
    });
    return arr.slice(0, 25);
  }, [picks, sort]);

  return (
    <div style={{
      background: T.surface1, border: `1px solid ${T.border2}`, borderRadius: 16,
      overflow: 'hidden', marginTop: 14,
    }}>
      <div style={{
        display: 'grid', gridTemplateColumns: '50px 1fr 100px 110px 100px 60px',
        gap: 12, padding: '11px 18px', background: T.surface2, borderBottom: `1px solid ${T.border1}`,
        fontSize: 10.5, fontWeight: 700, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.06em',
      }}>
        <div>Rank</div>
        <div>Ticker</div>
        <div style={{ textAlign: 'right' }}>{side === 'calls' ? 'Pred' : 'P(put)'}</div>
        <div style={{ textAlign: 'right' }}>Actual 10d</div>
        <div style={{ textAlign: 'right' }}>Opt P&L</div>
        <div style={{ textAlign: 'center' }}>Out</div>
      </div>
      {sorted.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', color: T.textMuted }}>No picks in this week</div>
      ) : sorted.map((p, i) => {
        const actual = parseFloat(p.out_max_up_7d || 0);
        const pred = parseFloat(p.pred_up_pct || 0);
        const pnl = parseFloat(p.option_pnl_est_pct || 0);
        const out = actual >= 30 ? { i: '🐲', c: T.gold } : actual >= 15 ? { i: '✅', c: T.green } : actual >= 5 ? { i: '✓', c: T.green } : actual >= 0 ? { i: '·', c: T.textMuted } : { i: '✗', c: T.red };
        const series = (p.prices_fwd14 && p.prices_fwd14.length >= 2) ? p.prices_fwd14 : [];
        const sparkColor = actual >= 0 ? '#5e9e7e' : T.red;
        const isTop3 = i < 3;
        return (
          <div key={i} style={{
            display: 'grid', gridTemplateColumns: '50px 1fr 100px 110px 100px 60px',
            gap: 12, padding: '11px 18px',
            borderBottom: `1px solid ${T.surface2}`,
            background: actual >= 30 ? 'rgba(251,191,36,.04)' : 'transparent',
            transition: 'background .15s',
            alignItems: 'center',
          }}>
            <div style={{ fontFamily: T.mono, fontSize: 12, fontWeight: 700, color: isTop3 ? T.gold : T.textMuted2 }}>#{i + 1}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontFamily: T.mono, fontSize: 14, fontWeight: 700, color: T.textBright }}>{p.ticker}</span>
              <Sparkline data={series} color={sparkColor} w={46} h={18} />
            </div>
            <div style={{ textAlign: 'right', fontFamily: T.mono, fontSize: 13, color: T.indigoLight }}>
              {side === 'calls' ? `+${pred.toFixed(1)}%` : (parseFloat(p.p_put || 0)).toFixed(3)}
            </div>
            <div style={{ textAlign: 'right', fontFamily: T.mono, fontSize: 13, color: out.c, fontWeight: 600 }}>
              {actual >= 0 ? '+' : ''}{actual.toFixed(1)}%
            </div>
            <div style={{ textAlign: 'right', fontFamily: T.mono, fontSize: 13, color: pnl >= 0 ? T.green : T.red }}>
              {pnl >= 0 ? '+' : ''}{pnl.toFixed(0)}%
            </div>
            <div style={{ textAlign: 'center', fontSize: 17, color: out.c }} title={`Outcome ${out.i}`}>{out.i}</div>
          </div>
        );
      })}
    </div>
  );
}

// -- Theoretical Trading Account panel --------------------------------------
function TradingAccountPanel({ trading }) {
  if (!trading || !trading.n_trades) return null;
  const t = trading;
  const avgPos = (t.avg_pnl_pct || 0) >= 0;
  const medPos = (t.median_pnl_pct || 0) >= 0;
  const cards = [
    { label: 'Win rate', icon: '', value: (t.win_rate || 0).toFixed(0), unit: '%', sub: t.n_trades + ' trades', color: T.textHi },
    { label: 'Avg P&L / trade', icon: '', value: (avgPos ? '+' : '') + (t.avg_pnl_pct || 0).toFixed(0), unit: '%', sub: 'equal-weighted', color: avgPos ? T.green : T.red, bd: avgPos ? T.border2 : T.border2 },
    { label: 'Median P&L', icon: '', value: (medPos ? '+' : '') + (t.median_pnl_pct || 0).toFixed(0), unit: '%', sub: 'typical trade', color: medPos ? T.green : T.red },
    { label: 'Big wins >=2x', icon: '', value: t.big_wins_2x || 0, unit: '', sub: 'incl. ' + (t.monster_3x || 0) + ' >=3x', color: T.gold, bd: T.borderWarm },
    { label: 'Stopped out', icon: '', value: t.big_losses_50 || 0, unit: '', sub: '<= -50%', color: T.red },
    { label: 'Best / worst', icon: '', value: '+' + (t.best_pnl_pct || 0).toFixed(0), unit: '%', sub: 'worst ' + (t.worst_pnl_pct || 0).toFixed(0) + '%', color: T.green },
  ];
  const reasons = t.exit_reasons || {};
  const totalR = Object.values(reasons).reduce((a, b) => a + b, 0) || 1;
  const rColor = { profit_target: T.green, stop_loss: T.red, time_stop: T.indigo, end_of_window: T.textMuted };
  return (
    <div style={{ marginTop: 16, background: T.surface2, border: '1px solid ' + T.border2, borderRadius: 18, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: T.textHi }}>{'💰'} Theoretical Options Account</div>
          <div style={{ fontSize: 11.5, color: T.textMuted2, marginTop: 3 }}>{t.exit_rule} {'·'} Feb-Jun 2026 backtest</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: T.mono, fontSize: 26, fontWeight: 800, color: avgPos ? T.green : T.red }}>
            {(avgPos ? '+' : '') + (t.avg_pnl_pct || 0).toFixed(1)}%
          </div>
          <div style={{ fontSize: 10.5, color: T.textMuted2 }}>avg return / trade</div>
        </div>
      </div>
      <div className="g-multi" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 10 }}>
        {cards.map((c, i) => <StatCard key={i} {...c} />)}
      </div>
      <div style={{ marginTop: 14 }}>
        <div style={{ display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden', border: '1px solid ' + T.border1 }}>
          {Object.entries(reasons).map(([k, v]) => (
            <div key={k} title={k + ': ' + v} style={{ width: (v / totalR * 100) + '%', background: rColor[k] || T.textMuted }} />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
          {Object.entries(reasons).map(([k, v]) => (
            <span key={k} style={{ fontSize: 11, color: T.textMuted2, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: rColor[k] || T.textMuted }} />
              {k.replace(/_/g, ' ')} {v} ({(v / totalR * 100).toFixed(0)}%)
            </span>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 12, fontSize: 11.5, color: T.textMuted, lineHeight: 1.5 }}>
        Buys the ATM option at each signal and exits by the rule above, using real Polygon option prices.
        Asymmetric profile: the median trade loses (most hit the stop), but {t.monster_3x || 0} winners of 3x or more carry the average positive.
      </div>
    </div>
  );
}

// ── Per-side stats panel (live-updates with the confidence slider) ─────────
function SideStatsPanel({ sideLabel, accent, open, closed }) {
  const marked = open.filter((r) => r.opt_pnl_pct != null);
  const pending = open.length - marked.length;
  const green = marked.filter((r) => r.opt_pnl_pct > 0).length;
  const avg = marked.length ? marked.reduce((a, r) => a + r.opt_pnl_pct, 0) / marked.length : 0;
  const cm = closed.filter((r) => r.opt_pnl_pct != null);
  const cw = cm.filter((r) => r.opt_pnl_pct > 0).length;
  const cavg = cm.length ? cm.reduce((a, r) => a + r.opt_pnl_pct, 0) / cm.length : 0;
  return (
    <div style={{ background: T.surface2, border: `1px solid ${T.border2}`, borderRadius: 16, padding: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: accent, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 10 }}>{sideLabel}</div>
      <div className="g-multi" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <StatCard label="Open" icon="" value={open.length} unit="" sub={`${pending} pending`} color={T.textHi} />
        <StatCard label="Unrealized" icon="" value={`${avg >= 0 ? '+' : ''}${avg.toFixed(1)}`} unit="%" sub="avg / marked" color={avg >= 0 ? T.green : T.red} />
        <StatCard label="In profit" icon="" value={`${green}/${marked.length}`} unit="" sub="open green" color={T.green} />
        <StatCard label="Closed wins" icon="" value={`${cw}/${cm.length}`} unit="" sub={`realized avg ${cavg >= 0 ? '+' : ''}${cavg.toFixed(0)}%`} color={T.indigo} />
      </div>
    </div>
  );
}

// ── Two-column trades: calls left, puts right ──────────────────────────────
function TwoColTrades({ trades, onSelect, todayView = false, volMap = {}, pickRanks = {}, onAnalyze, analyzing, viewName }) {
  const pkKey = (p) => `${p.ticker}|${p.date}|${p.side || 'call'}`;

  const [sortBy, setSortBy] = useState('default');
  const predVal = (x) => (x.side || 'call') === 'call' ? parseFloat(x.pred_up_pct || 0) : parseFloat(x.p_put || 0);
  const sortFn = (a, b) => {
    if (sortBy === 'pnl') return ((b.opt_pnl_pct == null ? -1e9 : b.opt_pnl_pct)) - ((a.opt_pnl_pct == null ? -1e9 : a.opt_pnl_pct));
    if (sortBy === 'pred') return predVal(b) - predVal(a);
    if (sortBy === 'promise') return ((pickRanks[pkKey(a)] || {}).rank || 999) - ((pickRanks[pkKey(b)] || {}).rank || 999);
    return 0;
  };
  const calls = trades.filter((r) => (r.side || 'call') === 'call').slice().sort(sortFn);
  const puts = trades.filter((r) => (r.side || 'call') !== 'call').slice().sort(sortFn);
  const SORTS = [['default', 'Confidence'], ['pnl', '% P&L'], ['pred', 'Prediction'], ['promise', '🎯 Promising']];
  const col = (list, label, color) => (
    <div>
      <div style={{ fontSize: 11, fontWeight: 800, color, letterSpacing: '.07em', textTransform: 'uppercase', marginBottom: 10 }}>{label} ({list.length})</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {list.length === 0
          ? <div style={{ padding: 18, textAlign: 'center', color: T.textFaint, background: T.surface1, borderRadius: 12, border: `1px solid ${T.border1}`, fontSize: 12 }}>none at this threshold</div>
          : list.map((p, i) => (
            <PickCard key={p.ticker + p.date + p.side + i} pick={p} rank={i + 1} highConv={todayView && i === 0} onOpen={(panel) => onSelect(p, panel)} todayView={todayView} vol={volMap[p.ticker]} />
          ))}
      </div>
    </div>
  );
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={() => { if (onAnalyze) onAnalyze(); setSortBy('promise'); }} disabled={analyzing} style={{
          padding: '6px 13px', borderRadius: 9, border: '1px solid rgba(129,140,248,.4)',
          background: 'linear-gradient(180deg,#14132b,#0e0d1f)', color: T.indigoLight, fontSize: 12.5, fontWeight: 800,
          cursor: analyzing ? 'wait' : 'pointer', fontFamily: 'inherit',
        }}>{analyzing ? '⏳ Analyzing…' : '🎯 Analyze picks'}</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 700 }}>Sort by</span>
        {SORTS.map(([k, l]) => (
          <button key={k} onClick={() => setSortBy(k)} style={{
            padding: '5px 11px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            border: `1px solid ${sortBy === k ? T.borderWarm : T.border2}`,
            background: sortBy === k ? 'linear-gradient(180deg,#221c11,#181208)' : T.surface1,
            color: sortBy === k ? T.gold : T.textMuted,
          }}>{l}</button>
        ))}
        </div>
      </div>
      <div className="g-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
        {col(calls, '📈 Calls', T.green)}
        {col(puts, '📉 Puts', T.red)}
      </div>
    </div>
  );
}

// ── Confidence threshold bar: sliders + live aggregates ────────────────────
function CardLegend() {
  const [open, setOpen] = useState(false);
  const rows = [
    ['CALL · profits if \u2191', 'The trade is a call (or put) option. Call = bets the stock goes up; put = bets it goes down.'],
    ['+21% if bought at first trade today', "Your live option profit: what you'd have if you bought this contract at its first trade this morning. The sparkline is that option price moving through the day."],
    ['entry $1.53 \u00b7 $34 call exp Jul 24', 'The exact contract: premium paid per share, the strike price, and the expiration date.'],
    ['stock \u221215.8% in 20d before pick', "Context about the setup \u2014 how the STOCK moved in the 20 days BEFORE the pick. This is history, not your trade's P&L."],
    ['\ud83e\udde0 pred +9% (10d max)', "The model's predicted best-case stock move over the next ~10 days."],
    ['score 14 \u00b7 tilt 2/4', 'Internal ranking strength (higher = stronger) and a 0\u20134 conviction tilt. Together they set the #rank.'],
    ['similar picks hit \u226515%: 44%', 'Calibration from history: of past picks that looked like this one, this share actually achieved a \u226515% move. Your honest odds.'],
    ['PT +31%', 'Wall Street analyst price target, expressed as % above the current price.'],
    ['RSI 23', 'Momentum gauge. Under 30 = oversold, over 70 = overbought.'],
  ];
  return (
    <div style={{ marginTop: 14 }}>
      <button onClick={() => setOpen((o) => !o)} style={{
        display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 13px', borderRadius: 10,
        border: `1px solid ${T.border2}`, background: T.surface1, color: T.textMid, fontSize: 12.5,
        fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
      }}>
        <span style={{ display: 'inline-flex', width: 18, height: 18, borderRadius: '50%', background: T.indigoLight, color: '#0b0e16', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 12 }}>?</span>
        What do these cards mean?
        <span style={{ opacity: .6 }}>{open ? '\u25b2' : '\u25bc'}</span>
      </button>
      {open && (
        <div style={{ marginTop: 10, padding: '14px 16px', borderRadius: 14, background: T.surface1, border: `1px solid ${T.border2}`, maxWidth: 760 }}>
          <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
            Quick rule: the <b style={{ color: T.green }}>green box</b> is your live option profit. Everything below it is the <b style={{ color: T.indigoLight }}>evidence &amp; history</b> explaining why the model made the bet.
          </div>
          {rows.map(([k, v], i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '210px 1fr', gap: 12, padding: '8px 0', borderTop: i ? `1px solid ${T.border1}` : 'none' }}>
              <div style={{ fontFamily: T.mono, fontSize: 11.5, color: T.textHi, fontWeight: 700 }}>{k}</div>
              <div style={{ fontSize: 12.5, color: T.textMid, lineHeight: 1.5 }}>{v}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ConfidenceBar({ minCall, minPut, onCall, onPut, trades, showCalls = true, showPuts = true, onToggleCalls, onTogglePuts }) {
  const withPnl = trades.filter((r) => r.opt_pnl_pct != null);
  const wins = withPnl.filter((r) => r.opt_pnl_pct > 0).length;
  const avg = withPnl.length ? withPnl.reduce((a, r) => a + r.opt_pnl_pct, 0) / withPnl.length : 0;
  const dollars = withPnl.reduce((a, r) => a + r.opt_pnl_pct * 10, 0); // $1,000 per trade
  const nc = trades.filter((r) => (r.side || 'call') === 'call').length;
  const np = trades.length - nc;
  return (
    <div style={{ margin: '18px 0 4px', padding: '13px 16px', borderRadius: 14, background: T.surface2, border: `1px solid ${T.border2}` }}>
      <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12, color: T.textMid }}>
          <button onClick={onToggleCalls} title="toggle calls" style={{ cursor: 'pointer', fontFamily: T.mono, fontWeight: 700, fontSize: 12, minWidth: 130, textAlign: 'left', padding: '4px 9px', borderRadius: 8, background: showCalls ? 'rgba(52,211,153,.12)' : T.surface1, border: `1px solid ${showCalls ? 'rgba(52,211,153,.35)' : T.border2}`, color: showCalls ? T.green : T.textFaint }}>{showCalls ? '☑' : '☐'} CALLS ≥ {minCall}%</button>
          <input type="range" min="0" max="40" step="1" value={minCall} onChange={(e) => onCall(+e.target.value)} style={{ accentColor: '#34d399', width: 130, cursor: 'pointer', opacity: showCalls ? 1 : .3 }} disabled={!showCalls} />
          <span style={{ fontSize: 10, color: T.textFaint }}>pred upside</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12, color: T.textMid }}>
          <button onClick={onTogglePuts} title="toggle puts" style={{ cursor: 'pointer', fontFamily: T.mono, fontWeight: 700, fontSize: 12, minWidth: 122, textAlign: 'left', padding: '4px 9px', borderRadius: 8, background: showPuts ? 'rgba(248,113,113,.12)' : T.surface1, border: `1px solid ${showPuts ? 'rgba(248,113,113,.35)' : T.border2}`, color: showPuts ? T.red : T.textFaint }}>{showPuts ? '☑' : '☐'} PUTS ≥ {minPut}%</button>
          <input type="range" min="0" max="40" step="1" value={minPut} onChange={(e) => onPut(+e.target.value)} style={{ accentColor: '#f87171', width: 130, cursor: 'pointer', opacity: showPuts ? 1 : .3 }} disabled={!showPuts} />
          <span style={{ fontSize: 10, color: T.textFaint }}>P(put)</span>
        </label>
        <div style={{ marginLeft: 'auto', fontFamily: T.mono, fontSize: 12.5, color: T.textMid, display: 'flex', gap: 15, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <span>{trades.length} trades <span style={{ color: T.textFaint, fontSize: 11 }}>({nc}C·{np}P)</span></span>
          <span>wins <b style={{ color: T.green }}>{wins}</b>/{withPnl.length} <span style={{ color: T.textFaint }}>({withPnl.length ? Math.round(wins / withPnl.length * 100) : 0}%)</span></span>
          <span>avg <b style={{ color: avg >= 0 ? T.green : T.red }}>{(avg >= 0 ? '+' : '') + avg.toFixed(1)}%</b></span>
          <span>P&L @ $1k/trade <b style={{ color: dollars >= 0 ? T.green : T.red }}>{(dollars >= 0 ? '+$' : '−$') + Math.abs(Math.round(dollars)).toLocaleString()}</b></span>
        </div>
      </div>
    </div>
  );
}

// ── Trade detail modal (click any card) ────────────────────────────────────
function TradeModal({ pick, onClose }) {
  const [freshNews, setFreshNews] = useState(null);
  useEffect(() => {
    if (!pick) return;
    let dead = false;
    (async () => {
      const out = [];
      try {
        const g = await axios.get(`${API_URL}/api/gdelt-news/${pick.ticker}`);
        (g.data.articles || []).forEach((a) => out.push({ time: a.time, source: a.source, headline: a.headline, url: a.url, tone: a.tone }));
      } catch (e) {}
      if (!dead) setFreshNews([...out]);
      try {
        const r = await axios.get(`${API_URL}/api/news/${pick.ticker}`);
        const live = r.data.news || r.data.articles || [];
        const seen = new Set(out.map((x) => (x.headline || '').toLowerCase().slice(0, 60)));
        live.forEach((a) => {
          const h = a.headline || a.title || '';
          if (!h || seen.has(h.toLowerCase().slice(0, 60))) return;
          out.push({ time: a.datetime || a.published || a.time || '', source: a.source, headline: h, url: a.url || a.link, summary: a.summary });
        });
      } catch (e) {}
      if (!dead) setFreshNews([...out]);
    })();
    return () => { dead = true; };
  }, [pick && pick.ticker]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);
  if (!pick) return null;
  const side = pick.side || 'call';
  const isCall = side === 'call';
  const pnl = pick.opt_pnl_pct;
  const hasPnl = pnl !== undefined && pnl !== null;
  const pos = hasPnl && pnl >= 0;
  const pnlColor = pos ? T.green : T.red;
  const reason = pick.opt_exit_reason;
  const open = pick.opt_open || reason === 'end_of_window';
  const optS = (pick.opt_series && pick.opt_series.length >= 2) ? pick.opt_series : [];
  const ov = optS.map((p) => (Array.isArray(p) ? p[1] : p));
  const ent = pick.opt_entry_price || (ov.length ? ov[0] : null);
  const badgeText = pick.opt_status === 'pending' ? '🟡 JUST OPENED' : open ? '🟢 HOLDING'
    : reason === 'profit_target' ? '✅ WON · TARGET' : reason === 'stop_loss' ? '🔴 LOST · STOPPED'
    : reason === 'time_stop' ? (pos ? '✅ WON · 7-DAY' : '🔴 LOST · 7-DAY') : 'CLOSED';
  const stats = [
    ['Opened', fmtDate(pick.date)],
    ['Days held', pick.opt_days_held != null ? pick.opt_days_held : '—'],
    ['Entry', ent != null ? '$' + ent : '—'],
    ['Now / exit', ov.length ? '$' + ov[ov.length - 1] : '—'],
    ['High', ov.length ? '$' + Math.max(...ov) : '—'],
    ['Low', ov.length ? '$' + Math.min(...ov) : '—'],
    ['Strike', pick.opt_strike ? '$' + pick.opt_strike : '—'],
    ['Expiry', pick.opt_expiry ? fmtDate(pick.opt_expiry) + (pick.opt_dte != null ? ' (' + pick.opt_dte + 'd)' : '') : '—'],
    ['Contract', pick.opt_contract ? pick.opt_contract.replace('O:', '') : '—'],
    [isCall ? 'Model target' : 'Model P(put)', isCall ? '+' + parseFloat(pick.pred_up_pct || 0).toFixed(0) + '%' : Math.round(parseFloat(pick.p_put || 0) * 100) + '%'],
    ['Score (at pick)', pick.tilted_score != null ? parseFloat(pick.tilted_score).toFixed(0) : '—'],
    ['Tilt signals', pick.tilt != null ? parseInt(pick.tilt, 10) + '/4' : '—'],
    ['Similar picks hit ≥15%', pick.ml_hit15_pct != null ? pick.ml_hit15_pct + '%' : '—'],
    ['Updated', pick.opt_live_asof ? String(pick.opt_live_asof).slice(11, 16) + ' live' : 'daily close'],
  ];
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(4,6,10,.78)', backdropFilter: 'blur(3px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface1, border: `1px solid ${T.border2}`, borderRadius: 20, padding: 24, width: 'min(960px, 96vw)', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: T.mono, fontSize: 30, fontWeight: 800, color: T.textBright }}>{pick.ticker}</span>
          <span style={{ padding: '4px 10px', borderRadius: 7, fontSize: 11.5, fontWeight: 700, background: isCall ? 'rgba(52,211,153,.13)' : 'rgba(248,113,113,.13)', color: isCall ? T.green : T.red, border: `1px solid ${isCall ? 'rgba(52,211,153,.3)' : 'rgba(248,113,113,.3)'}` }}>{isCall ? 'CALL · profits if ↑' : 'PUT · profits if ↓'}</span>
          {hasPnl && <span style={{ fontSize: 11, fontWeight: 800, padding: '4px 10px', borderRadius: 7, color: pnlColor, background: pnlColor + '1f', border: `1px solid ${pnlColor}44` }}>{badgeText}</span>}
          <button onClick={onClose} style={{ marginLeft: 'auto', background: T.surface2, border: `1px solid ${T.border2}`, color: T.textMid, borderRadius: 9, padding: '7px 13px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>✕ Close</button>
        </div>
        {hasPnl && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 14 }}>
            <span style={{ fontFamily: T.mono, fontSize: 42, fontWeight: 800, color: pnlColor, letterSpacing: '-.02em' }}>{(pos ? '+' : '') + Number(pnl).toFixed(1)}%</span>
            <span style={{ fontSize: 12, color: T.textMuted2 }}>option P&L{pick.opt_modeled ? ' · est (modeled)' : ''}{open ? ' · live' : ' · realized'}</span>
          </div>
        )}
        {optS.length >= 2 ? (
          <>
            <AreaChart data={optS} color={hasPnl ? pnlColor : T.gold} w={880} h={300} baseline={ent} anchors={pick.opt_anchors} />
            <div style={{ fontSize: 11, color: T.textFaint, fontFamily: T.mono, textAlign: 'right', marginTop: 4 }}>
              {pick.opt_modeled ? `value modeled from stock 5-min moves · rings = ${(pick.opt_anchors || []).length} real option trades · dashed = entry` : 'option price · 1-min trades · dashed = entry'}
            </div>
          </>
        ) : (
          <div style={{ padding: 40, textAlign: 'center', color: T.textMuted, background: T.surface2, borderRadius: 12 }}>No option price data yet — P&L starts on the contract's first trade.</div>
        )}
        <div className="g-multi" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginTop: 16 }}>
          {stats.map(([label, value], i) => (
            <div key={i} style={{ background: T.surface2, border: `1px solid ${T.border1}`, borderRadius: 12, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>{label}</div>
              <div style={{ fontFamily: T.mono, fontSize: 16, fontWeight: 700, color: T.textHi, marginTop: 3 }}>{value}</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 10.5, color: T.cyan, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 }}>📰 Latest news · GDELT 48h + live</div>
          {freshNews === null ? (
            <div style={{ fontSize: 12, color: T.textMuted, padding: '8px 0' }}>Loading news…</div>
          ) : freshNews.length === 0 ? (
            <div style={{ fontSize: 12, color: T.textMuted, padding: '8px 0' }}>No recent articles found.</div>
          ) : (
            freshNews.slice(0, 10).map((a, i) => (
              <a key={i} href={a.url} target="_blank" rel="noreferrer" style={{ display: 'block', textDecoration: 'none', padding: '7px 0', borderBottom: `1px solid ${T.border1}` }}>
                <span style={{ fontFamily: T.mono, fontSize: 10, color: T.textFaint }}>{(() => {
                  const raw = String(a.time || '').trim().replace(' ', 'T');
                  if (!raw) return '';
                  const d = new Date(raw + (raw.length === 16 ? ':00Z' : raw.endsWith('Z') ? '' : 'Z'));
                  return isNaN(d.getTime()) ? a.time : d.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) + ' ET';
                })()}</span>{' '}
                <span style={{ fontSize: 10.5, color: T.cyan, fontWeight: 600 }}>{a.source}</span>{' '}
                {a.tone != null && <span style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 700, color: a.tone >= 0 ? '#34d399' : '#f87171' }}>{a.tone >= 0 ? '▲' : '▼'}{Math.abs(a.tone).toFixed(1)}</span>}
                <div style={{ fontSize: 12.5, color: '#dbe3ee', marginTop: 2, lineHeight: 1.4 }}>{a.headline}</div>
              </a>
            ))
          )}
        </div>

        {pick.ai_rationale && (
          <div style={{ marginTop: 16, padding: '13px 15px', borderRadius: 12, background: 'rgba(251,191,36,.05)', border: '1px solid rgba(251,191,36,.18)' }}>
            <div style={{ fontSize: 10.5, color: T.gold, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 6 }}>💡 Why this trade</div>
            <div style={{ fontSize: 13, color: T.textMid, lineHeight: 1.55 }}>{pick.ai_rationale}</div>
          </div>
        )}
        {pick.ai_evidence && (() => {
          const ev = pick.ai_evidence;
          const t = ev.technicals || {};
          const soc = ev.social || {};
          const tiles = [
            ['RSI', t.rsi != null ? Math.round(parseFloat(t.rsi)) : '—'],
            ['Day gap', t.gap_pct != null ? parseFloat(t.gap_pct).toFixed(1) + '%' : '—'],
            ['Volume z', t.vol_zscore != null ? parseFloat(t.vol_zscore).toFixed(1) : '—'],
            ['Analyst PT', ev.analyst_pt_upside_pct != null ? (parseFloat(ev.analyst_pt_upside_pct) >= 0 ? '+' : '') + parseFloat(ev.analyst_pt_upside_pct).toFixed(0) + '%' : '—'],
            ['Earnings in', ev.days_to_earnings != null && ev.days_to_earnings !== '' ? parseFloat(ev.days_to_earnings).toFixed(0) + 'd' : '—'],
            ['Premkt gap', ev.premarket_gap_pct != null && ev.premarket_gap_pct !== '' ? parseFloat(ev.premarket_gap_pct).toFixed(1) + '%' : '—'],
            ['Unusual calls', ev.unusual_call_buying ? 'YES' : 'no'],
            ['StockTwits', soc.stocktwits_msgs ? `${soc.st_bullish}↑/${soc.st_bearish}↓ of ${soc.stocktwits_msgs}` : '—'],
          ];
          return (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 10.5, color: T.textMuted2, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 8 }}>Evidence at pick time</div>
              <div className="g-multi" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                {tiles.map(([label, value], i) => (
                  <div key={i} style={{ background: T.surface2, border: `1px solid ${T.border1}`, borderRadius: 12, padding: '9px 11px' }}>
                    <div style={{ fontSize: 9.5, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>{label}</div>
                    <div style={{ fontFamily: T.mono, fontSize: 14.5, fontWeight: 700, color: T.textHi, marginTop: 2 }}>{value}</div>
                  </div>
                ))}
              </div>
              {ev.news && ev.news.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 10.5, color: T.textMuted2, fontWeight: 700, letterSpacing: '.05em', marginBottom: 4 }}>NEWS THE MODEL SAW AT PICK TIME</div>
                  {ev.news.map((n, i) => (
                    <a key={i} href={n.url} target="_blank" rel="noreferrer" style={{ display: 'block', fontSize: 12, color: T.blue2, textDecoration: 'none', padding: '5px 0', borderBottom: `1px solid ${T.border1}` }}>
                      <span style={{ color: T.textFaint, fontFamily: T.mono, fontSize: 10.5 }}>{n.date}</span> {n.title}
                    </a>
                  ))}
                </div>
              )}
              {soc.reddit_top_post && (
                <div style={{ marginTop: 8, fontSize: 11.5, color: T.textMuted }}>Reddit ({soc.reddit_posts} posts): “{soc.reddit_top_post}”</div>
              )}
            </div>
          );
        })()}
        <div style={{ marginTop: 14, fontSize: 11.5, color: T.textMuted }}>
          Exit rule: take-profit +100% · stop −50% · 7-trading-day time-stop. {open ? 'Position is still open.' : 'Position closed.'}
        </div>
      </div>
    </div>
  );
}

function DockTradeNews({ ticker }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let dead = false;
    setItems(null);
    (async () => {
      const out = [];
      try {
        const g = await axios.get(`${API_URL}/api/gdelt-news/${ticker}`);
        (g.data.articles || []).forEach((a) => out.push({ time: a.time, source: a.source, headline: a.headline, url: a.url, tone: a.tone }));
      } catch (e) {}
      if (!dead) setItems([...out]);
      try {
        const r = await axios.get(`${API_URL}/api/news/${ticker}`);
        const live = r.data.news || r.data.articles || [];
        const seen = new Set(out.map((x) => (x.headline || '').toLowerCase().slice(0, 60)));
        live.forEach((a) => {
          const h = a.headline || a.title || '';
          if (!h || seen.has(h.toLowerCase().slice(0, 60))) return;
          out.push({ time: a.datetime || a.published || a.time || '', source: a.source, headline: h, url: a.url || a.link, summary: a.summary });
        });
      } catch (e) {}
      if (!dead) setItems([...out]);
    })();
    return () => { dead = true; };
  }, [ticker]);
  if (items === null) return <div style={{ padding: 14, color: T.textMuted, fontSize: 12 }}>Loading news…</div>;
  if (items.length === 0) return <div style={{ padding: 14, color: T.textMuted, fontSize: 12 }}>No recent articles found.</div>;
  return (
    <div style={{ padding: '4px 10px 12px', maxHeight: 440, overflowY: 'auto' }}>
      {items.map((a, i) => (
        <a key={i} href={a.url} target="_blank" rel="noreferrer" style={{ display: 'block', textDecoration: 'none', margin: '8px 2px 0', padding: '9px 11px', borderRadius: 10, background: T.surface2, border: `1px solid ${T.border1}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 10.5, color: T.cyan, fontWeight: 700 }}>{a.source}</span>
            <span style={{ fontFamily: T.mono, fontSize: 10, color: T.textFaint, whiteSpace: 'nowrap' }}>{(() => {
              const raw = String(a.time || '').trim().replace(' ', 'T');
              if (!raw) return '';
              const d = new Date(raw + (raw.length === 16 ? ':00Z' : raw.endsWith('Z') ? '' : 'Z'));
              return isNaN(d.getTime()) ? a.time : d.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) + ' ET';
            })()}{a.tone != null ? (a.tone >= 0 ? ' ▲' : ' ▼') : ''}</span>
          </div>
          <div style={{ fontSize: 12.5, color: a.tone != null ? (a.tone >= 0 ? '#7ee2b8' : '#f2a3a3') : '#dbe3ee', marginTop: 4, lineHeight: 1.45 }}>{a.headline}</div>
        </a>
      ))}
    </div>
  );
}

function optionCandles(series) {
  // Bucket the option price series (entry -> now) into ~70 candles for the D3 chart.
  const pts = (series || []).map((p, i) => (Array.isArray(p) ? { t: p[0], v: p[1] } : { t: Date.now() - (series.length - i) * 60000, v: p }));
  const clean = pts.filter((p) => p.v != null && isFinite(p.v));
  if (clean.length < 2) return [];
  const target = Math.min(clean.length, 70);
  const per = Math.ceil(clean.length / target);
  const candles = [];
  for (let i = 0; i < clean.length; i += per) {
    const grp = clean.slice(i, i + per);
    const vals = grp.map((g) => g.v);
    candles.push({
      datetime: grp[grp.length - 1].t,
      open: grp[0].v, high: Math.max(...vals), low: Math.min(...vals),
      close: grp[grp.length - 1].v, volume: 0,
    });
  }
  return candles;
}

function TradeDock({ pick, panels, onClose, onCloseAll, pickRanks = {} }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCloseAll(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCloseAll]);
  if (!pick || (!panels.chart && !panels.news && !panels.details)) return null;
  const side = pick.side || 'call';
  const isCall = side === 'call';
  const pnl = pick.opt_pnl_pct;
  const hasPnl = pnl !== undefined && pnl !== null;
  const pos = hasPnl && pnl >= 0;
  const pnlColor = pos ? T.green : T.red;
  const reason = pick.opt_exit_reason;
  const open = pick.opt_open || reason === 'end_of_window';
  const optS = (pick.opt_series && pick.opt_series.length >= 2) ? pick.opt_series : [];
  const ov = optS.map((p) => (Array.isArray(p) ? p[1] : p));
  const ent = pick.opt_entry_price || (ov.length ? ov[0] : null);
  const badgeText = pick.opt_status === 'pending' ? '🟡 JUST OPENED' : open ? '🟢 HOLDING'
    : reason === 'profit_target' ? '✅ WON · TARGET' : reason === 'stop_loss' ? '🔴 LOST · STOPPED'
    : reason === 'time_stop' ? (pos ? '✅ WON · 7-DAY' : '🔴 LOST · 7-DAY') : 'CLOSED';
  const stats = [
    ['Opened', fmtDate(pick.date)],
    ['Days held', pick.opt_days_held != null ? pick.opt_days_held : '—'],
    ['Entry', ent != null ? '$' + ent : '—'],
    ['Now / exit', ov.length ? '$' + ov[ov.length - 1] : '—'],
    ['High', ov.length ? '$' + Math.max(...ov) : '—'],
    ['Low', ov.length ? '$' + Math.min(...ov) : '—'],
    ['Strike', pick.opt_strike ? '$' + pick.opt_strike : '—'],
    ['Expiry', pick.opt_expiry ? fmtDate(pick.opt_expiry) + (pick.opt_dte != null ? ' (' + pick.opt_dte + 'd)' : '') : '—'],
    [isCall ? 'Model target' : 'Model P(put)', isCall ? '+' + parseFloat(pick.pred_up_pct || 0).toFixed(0) + '%' : Math.round(parseFloat(pick.p_put || 0) * 100) + '%'],
    ['Score (at pick)', pick.tilted_score != null ? parseFloat(pick.tilted_score).toFixed(0) : '—'],
    ['Tilt signals', pick.tilt != null ? parseInt(pick.tilt, 10) + '/4' : '—'],
    ['Hit ≥15%', pick.ml_hit15_pct != null ? pick.ml_hit15_pct + '%' : '—'],
  ];
  const card = (icon, title, key, body) => (
    <div style={{ borderBottom: `1px solid ${T.border2}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px' }}>
        <span style={{ fontSize: 13 }}>{icon}</span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: T.textHi }}>{title}</span>
        <button onClick={() => onClose(key)} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: T.textMuted, fontSize: 14, cursor: 'pointer' }}>✕</button>
      </div>
      {body}
    </div>
  );
  return (
    <div style={{ position: 'fixed', top: 62, right: 0, bottom: 0, width: 400, maxWidth: '95vw', zIndex: 90, background: T.surface1, borderLeft: `1px solid ${T.border2}`, boxShadow: '-16px 0 40px rgba(0,0,0,.45)', overflowY: 'auto', paddingBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 14px 6px', flexWrap: 'wrap' }}>
        <span style={{ fontFamily: T.mono, fontSize: 20, fontWeight: 800, color: T.textBright }}>{pick.ticker}</span>
        <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 10.5, fontWeight: 700, background: isCall ? 'rgba(52,211,153,.13)' : 'rgba(248,113,113,.13)', color: isCall ? T.green : T.red, border: `1px solid ${isCall ? 'rgba(52,211,153,.3)' : 'rgba(248,113,113,.3)'}` }}>{isCall ? 'CALL · profits if ↑' : 'PUT · profits if ↓'}</span>
        <button onClick={onCloseAll} style={{ marginLeft: 'auto', background: T.surface2, border: `1px solid ${T.border2}`, color: T.textMid, borderRadius: 9, padding: '5px 11px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Close all ✕</button>
      </div>

      {panels.chart && card('📈', 'Chart since entry', 'chart', (
        <div style={{ padding: '4px 14px 14px' }}>
          {hasPnl && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
              <span style={{ fontFamily: T.mono, fontSize: 26, fontWeight: 800, color: pnlColor }}>{(pos ? '+' : '') + Number(pnl).toFixed(1)}%</span>
              <span style={{ fontSize: 10.5, color: T.textMuted2 }}>option P&L{pick.opt_modeled ? ' · modeled' : ''}{open ? ' · live' : ' · realized'}</span>
              <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 800, padding: '3px 8px', borderRadius: 6, color: pnlColor, background: pnlColor + '1f', border: `1px solid ${pnlColor}44`, whiteSpace: 'nowrap' }}>{badgeText}</span>
            </div>
          )}
          {optS.length >= 2 ? (
            <>
              <D3CandlestickChart chartData={{ candles: optionCandles(optS) }} baseline={ent} hideHeader />
              <div style={{ fontSize: 9.5, color: T.textFaint, fontFamily: T.mono, marginTop: 4, lineHeight: 1.4 }}>
                {pick.opt_modeled ? 'option value modeled from stock 5-min moves · dashed = entry · drag to zoom' : 'option price since entry · dashed = entry · drag to zoom'}
              </div>
              <div style={{ fontSize: 11, color: T.textMuted, fontFamily: T.mono, marginTop: 6 }}>
                entry ${ent} → {ov.length ? '$' + ov[ov.length - 1] : '—'} · opened {fmtDate(pick.date)}{pick.opt_days_held != null ? ` · day ${pick.opt_days_held}` : ''}
              </div>
            </>
          ) : <div style={{ padding: 24, textAlign: 'center', color: T.textMuted, fontSize: 12, background: T.surface2, borderRadius: 10 }}>No option prints yet — P&L starts on the contract's first trade.</div>}
        </div>
      ))}

      {panels.news && card('📰', `${pick.ticker} News`, 'news', <DockTradeNews ticker={pick.ticker} />)}

      {panels.details && card('📋', 'Trade details', 'details', (
        <div style={{ padding: '6px 14px 14px' }}>
          {(() => {
            const pr = pickRanks[`${pick.ticker}|${pick.date}|${pick.side || 'call'}`];
            if (!pr) return null;
            const cc = pr.conviction === 'high' ? T.green : pr.conviction === 'medium' ? T.gold : T.textMuted;
            return (
              <div style={{ marginBottom: 12, padding: '11px 13px', borderRadius: 11, background: 'rgba(129,140,248,.06)', border: '1px solid rgba(129,140,248,.2)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, color: T.indigoLight, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase' }}>🎯 Most promising · #{pr.rank}</span>
                  {pr.conviction && <span style={{ fontSize: 10, fontWeight: 800, color: cc, textTransform: 'uppercase' }}>{pr.conviction}</span>}
                </div>
                {pr.reason && <div style={{ fontSize: 12.5, color: T.textHi, fontWeight: 600, marginBottom: 4 }}>{pr.reason}</div>}
                {pr.explanation && <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.55 }}>{pr.explanation}</div>}
              </div>
            );
          })()}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {stats.map(([label, value], i) => (
              <div key={i} style={{ background: T.surface2, border: `1px solid ${T.border1}`, borderRadius: 10, padding: '8px 10px' }}>
                <div style={{ fontSize: 9, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>{label}</div>
                <div style={{ fontFamily: T.mono, fontSize: 14, fontWeight: 700, color: T.textHi, marginTop: 2 }}>{value}</div>
              </div>
            ))}
          </div>
          {pick.ai_rationale && (
            <div style={{ marginTop: 12, padding: '11px 13px', borderRadius: 11, background: 'rgba(251,191,36,.05)', border: '1px solid rgba(251,191,36,.18)' }}>
              <div style={{ fontSize: 10, color: T.gold, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 5 }}>💡 Why this trade</div>
              <div style={{ fontSize: 12.5, color: T.textMid, lineHeight: 1.55 }}>{pick.ai_rationale}</div>
            </div>
          )}
          {pick.ai_evidence && pick.ai_evidence.news && pick.ai_evidence.news.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 10, color: T.textMuted2, fontWeight: 700, letterSpacing: '.05em', marginBottom: 4, textTransform: 'uppercase' }}>News the model saw at pick time</div>
              {pick.ai_evidence.news.map((n, i) => (
                <a key={i} href={n.url} target="_blank" rel="noreferrer" style={{ display: 'block', fontSize: 11.5, color: T.blue2, textDecoration: 'none', padding: '5px 0', borderBottom: `1px solid ${T.border1}` }}>
                  <span style={{ color: T.textFaint, fontFamily: T.mono, fontSize: 10 }}>{n.date}</span> {n.title}
                </a>
              ))}
            </div>
          )}
          <div style={{ marginTop: 12, fontSize: 10.5, color: T.textMuted, lineHeight: 1.5 }}>
            Exit rule: TP +100% · SL −50% · 7-day time-stop. {open ? 'Still open.' : 'Closed.'}
          </div>
        </div>
      ))}
    </div>
  );
}

function PicksAnalysisModal({ data, loading, onRerun, onClose, onOpen, trades }) {
  if (!data) return null;
  const ranked = data.ranked || [];
  const byKey = {};
  (trades || []).forEach((t) => { byKey[`${t.ticker}|${t.date}|${t.side || 'call'}`] = t; });
  const convColor = (c) => (c === 'high' ? T.green : c === 'medium' ? T.gold : T.textMuted);
  const title = data.view === 'open' ? 'Open Positions' : "Today's Picks";
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(4,6,10,.78)', backdropFilter: 'blur(3px)', zIndex: 120, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 18px', overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(900px, 96vw)', background: T.surface1, border: `1px solid ${T.border2}`, borderRadius: 18, boxShadow: '0 30px 90px rgba(0,0,0,.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 22px', borderBottom: `1px solid ${T.border2}` }}>
          <span style={{ fontSize: 18 }}>🎯</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.textBright }}>Most Promising — {title}</div>
            <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>
              {loading ? 'Analyzing ML + news + social + technicals' + (data.view === 'open' ? ' + live performance…' : '…')
                : <>Ranked {ranked.length} by AI across all signals{data.view === 'open' ? ' incl. live P&L' : ''}{data.cached ? ' · cached' : ''}</>}
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button onClick={onRerun} disabled={loading} style={{ background: T.surface2, border: '1px solid rgba(129,140,248,.4)', color: T.indigoLight, borderRadius: 9, padding: '5px 12px', fontSize: 12.5, fontWeight: 700, cursor: loading ? 'wait' : 'pointer', fontFamily: 'inherit' }}>{loading ? '⏳ Running…' : '🔄 Re-run'}</button>
            <button onClick={onClose} style={{ background: T.surface2, border: `1px solid ${T.border2}`, color: T.textMid, borderRadius: 9, padding: '5px 12px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Close ✕</button>
          </div>
        </div>
        <div style={{ padding: '14px 18px 20px', maxHeight: '68vh', overflowY: 'auto' }}>
          {data.error && <div style={{ color: T.red, fontSize: 13, padding: 12 }}>{data.error}</div>}
          {loading && !ranked.length && <div style={{ padding: 30, textAlign: 'center', color: T.textMuted, fontSize: 13 }}>⏳ Ranking picks…</div>}
          {ranked.map((r) => {
            const t = byKey[r.key] || {};
            const pnl = t.opt_pnl_pct;
            return (
              <div key={r.key} onClick={() => t.ticker && onOpen(t)} style={{ display: 'grid', gridTemplateColumns: '34px 92px 1fr auto', alignItems: 'start', gap: 12, padding: '11px 13px', margin: '7px 0', borderRadius: 12, cursor: t.ticker ? 'pointer' : 'default', background: T.surface2, border: `1px solid ${r.conviction === 'high' ? 'rgba(52,211,153,.28)' : T.border1}` }}>
                <span style={{ fontFamily: T.mono, fontWeight: 800, color: T.indigoLight, fontSize: 13 }}>#{r.rank}</span>
                <div>
                  <div style={{ fontFamily: T.mono, fontWeight: 800, color: T.textBright, fontSize: 15 }}>{r.ticker}</div>
                  <span style={{ fontSize: 9.5, fontWeight: 700, color: r.side === 'call' ? T.green : T.red, textTransform: 'uppercase' }}>{r.side}</span>
                  {pnl != null && <div style={{ fontFamily: T.mono, fontSize: 11, fontWeight: 700, color: pnl >= 0 ? T.green : T.red, marginTop: 2 }}>{pnl >= 0 ? '+' : ''}{Number(pnl).toFixed(0)}%</div>}
                </div>
                <div>
                  <div style={{ fontSize: 12.5, color: T.textHi, fontWeight: 600, lineHeight: 1.4 }}>{r.reason}</div>
                  {r.explanation && <div style={{ fontSize: 11.5, color: T.textMuted, lineHeight: 1.5, marginTop: 3 }}>{r.explanation}</div>}
                </div>
                <span style={{ fontSize: 10, fontWeight: 800, color: convColor(r.conviction), textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{r.conviction}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main Trending Tab ──────────────────────────────────────────────────────
export default function TrendingV2() {
  const [data, setData] = useState(null);
  const [heroLive, setHeroLive] = useState(null);
  const [volMap, setVolMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [weekSide, setWeekSide] = useState('calls');
  const [weekSort, setWeekSort] = useState('actual');
  const [showLower, setShowLower] = useState(false);
  const [openSort, setOpenSort] = useState('pnl');
  const [view, setView] = useState('open');
  const [closedCutoff, setClosedCutoff] = useState('all');
  const FILTER_DEFAULT = { mc: 0, mp: 0, sc: true, sp: true };
  const [filters, setFilters] = useState(() => {
    try { return JSON.parse(localStorage.getItem('mf_view_filters')) || {}; } catch (e) { return {}; }
  });
  const curF = { ...FILTER_DEFAULT, ...(filters[view] || {}) };
  const setF = (patch) => setFilters((old) => {
    const next = { ...old, [view]: { ...FILTER_DEFAULT, ...(old[view] || {}), ...patch } };
    try { localStorage.setItem('mf_view_filters', JSON.stringify(next)); } catch (e) {}
    return next;
  });
  const [dockPick, setDockPick] = useState(null);
  const [pickRanks, setPickRanks] = useState({});
  const [pickBusy, setPickBusy] = useState(null); // 'open' | 'today' while analyzing
  const [pickModal, setPickModal] = useState(null);
  useEffect(() => {
    try { const raw = localStorage.getItem('mf_pick_ranks'); if (raw) setPickRanks(JSON.parse(raw)); } catch (e) {}
  }, []);
  const analyzePicks = async (view, force = false) => {
    const trades = view === 'open' ? (data && data.open_trades || []) : (data && data.live_picks || []);
    if (!trades.length) return;
    setPickBusy(view);
    setPickModal({ view, loading: true });
    try {
      const r = await axios.post(`${API_URL}/api/analyze-picks`, { view, force, picks: trades });
      setPickModal({ view, ...r.data });
      setPickRanks((prev) => {
        const m = { ...prev };
        (r.data.ranked || []).forEach((x) => { if (x.key && x.rank) m[x.key] = x; });
        try { localStorage.setItem('mf_pick_ranks', JSON.stringify(m)); } catch (e) {}
        return m;
      });
    } catch (e) {
      setPickModal({ view, error: (e.response && e.response.data && e.response.data.error) || e.message });
    } finally { setPickBusy(null); }
  };
  const [dockPanels, setDockPanels] = useState({ chart: false, news: false, details: false });
  const openTradeDock = (pick, panel = 'chart') => {
    // one click = one panel: clicking news shows news, chart shows chart (no stacking surprises)
    setDockPick(pick);
    setDockPanels({ chart: false, news: false, details: false, [panel]: true });
  };
  const [spinning, setSpinning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/monsters`);
      setData(r.data);
      try { const hl = await axios.get(`${API_URL}/api/hero-live`); setHeroLive(hl.data); } catch (e) {}
      setErr(null);
      if (r.data?.weekly?.length) {
        setSelectedWeek(r.data.weekly[r.data.weekly.length - 1].week);
      }
    } catch (e) { setErr(e.message || String(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!data) return undefined;
    let stop = false;
    const syms = Array.from(new Set([...(data.live_picks || []), ...(data.open_trades || [])].map((p) => p.ticker).filter(Boolean)));
    if (!syms.length) return undefined;
    const tick = async () => {
      if (stop || document.hidden) return;
      try {
        const r = await axios.post(`${API_URL}/api/current-prices`, { symbols: syms });
        const m = {};
        Object.entries(r.data || {}).forEach(([k, v]) => { if (v && v.volume) m[k] = v.volume; });
        if (!stop) setVolMap(m);
      } catch (e) {}
    };
    tick();
    const iv = setInterval(tick, 60000);
    return () => { stop = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data && data.generated_at]);
  useEffect(() => {
    const t = setInterval(() => { load(); }, 300000);
    return () => clearInterval(t);
  }, []);

  const refresh = async () => {
    setSpinning(true);
    await load();
    setTimeout(() => setSpinning(false), 600);
  };

  if (loading && !data) {
    return <div style={{ padding: 60, textAlign: 'center', color: T.textMuted }}>Loading monster picks…</div>;
  }
  if (err) {
    return <div style={{ padding: 60, textAlign: 'center', color: T.red }}>{err}</div>;
  }
  if (!data) return null;

  const live = data.live_picks || [];
  const liveCalls = live.filter((p) => p.side === 'call').sort((a, b) => parseFloat(b.tilted_score || 0) - parseFloat(a.tilted_score || 0));
  const livePuts = live.filter((p) => p.side === 'put').sort((a, b) => parseFloat(b.p_put || 0) - parseFloat(a.p_put || 0));
  const heroPick = liveCalls[0];
  const openByTicker = {};
  (data.open_trades || []).forEach((r) => { if (r.date < data.latest_date && r.opt_pnl_pct != null && !(r.ticker in openByTicker)) openByTicker[r.ticker] = r; });
  const heldForHero = heroPick ? openByTicker[heroPick.ticker] : null;
  const markedOpen = (data.open_trades || []).filter((r) => r.opt_pnl_pct != null);
  const bestOpen = markedOpen.length ? markedOpen.reduce((a, b) => (b.opt_pnl_pct > a.opt_pnl_pct ? b : a)) : null;
  const _hlCall = heroLive && heroLive.call && heroPick && heroLive.call.ticker === heroPick.ticker ? heroLive.call : null;
  const heroLiveData = heroPick && heroPick.opt_pnl_pct != null ? (() => {
    const ser = heroPick.opt_series || [];
    const lastP = ser.length ? (Array.isArray(ser[ser.length - 1]) ? ser[ser.length - 1][1] : ser[ser.length - 1]) : null;
    return { pending: false, pnl_pct: heroPick.opt_pnl_pct, entry: heroPick.opt_entry_price, current: lastP,
             series: ser, stock_series: _hlCall ? _hlCall.stock_series : null, stock_change_pct: _hlCall ? _hlCall.stock_change_pct : null,
             strike: heroPick.opt_strike, expiry: heroPick.opt_expiry };
  })() : _hlCall;
  const heroAsOf = heroPick && heroPick.opt_live_asof ? heroPick.opt_live_asof : (heroLive ? heroLive.generated_at : null);
  const topPut = livePuts.length ? livePuts[0] : null;
  const livePutData = topPut && topPut.opt_pnl_pct != null
    ? { ticker: topPut.ticker, pnl_pct: topPut.opt_pnl_pct, strike: topPut.opt_strike, expiry: topPut.opt_expiry, pending: false }
    : (heroLive ? heroLive.put : null);
  const highConvCalls = liveCalls.filter((p) => parseFloat(p.pred_up_pct) >= 20);
  const lowConvCalls = liveCalls.filter((p) => parseFloat(p.pred_up_pct) < 20);

  const agg = data.aggregate || {};
  const trading = data.trading || {};
  const openTrades = data.open_trades || [];
  const sortedOpen = (() => {
    const marked = openTrades.filter((p) => p.opt_pnl_pct != null);
    const pending = openTrades.filter((p) => p.opt_pnl_pct == null);
    const byDate = (a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0) || (parseFloat(b.pred_up_pct || 0) - parseFloat(a.pred_up_pct || 0));
    if (openSort === 'date') return [...openTrades].sort(byDate);
    const cmp = openSort === 'pnl' ? (a, b) => b.opt_pnl_pct - a.opt_pnl_pct
      : openSort === 'pnl_asc' ? (a, b) => a.opt_pnl_pct - b.opt_pnl_pct
      : (a, b) => parseFloat(b.pred_up_pct || 0) - parseFloat(a.pred_up_pct || 0);
    marked.sort(cmp); pending.sort(byDate);
    return [...marked, ...pending];
  })();
  const confPass = (r) => ((r.side || 'call') === 'call'
    ? (curF.sc && parseFloat(r.pred_up_pct || 0) >= curF.mc)
    : (curF.sp && parseFloat(r.p_put || 0) * 100 >= curF.mp));
  const openF = sortedOpen.filter(confPass);
  const todayF = [...liveCalls, ...livePuts].filter(confPass);
  const openCallsF = openF.filter((r) => (r.side || 'call') === 'call');
  const openPutsF = openF.filter((r) => (r.side || 'call') !== 'call');
  const closedCallsF = (data.recently_closed || []).filter(confPass).filter((r) => (r.side || 'call') === 'call');
  const closedPutsF = (data.recently_closed || []).filter(confPass).filter((r) => (r.side || 'call') !== 'call');
  const recentlyClosed = data.recently_closed || [];
  const openBook = data.open_book || {};
  const stats = [
    { label: 'Monster rate', icon: '🐲', value: (agg.hit_rate_30pct || 0).toFixed(1), unit: '%', sub: `${agg.hits_30pct} / ${agg.total_calls} ≥30%`, color: T.gold, bd: T.borderWarm },
    { label: '5%+ hit', icon: '📈', value: (agg.hit_rate_5pct || 0).toFixed(1), unit: '%', sub: `${agg.hits_5pct} / ${agg.total_calls}`, color: T.green },
    { label: 'Avg predicted', icon: '🎯', value: `+${(agg.avg_pred || 0).toFixed(0)}`, unit: '%', sub: 'max 10-day', color: T.indigo },
    { label: 'Avg actual', icon: '✅', value: `+${(agg.avg_actual || 0).toFixed(0)}`, unit: '%', sub: 'realized', color: T.green },
    { label: 'Total picks', icon: '🗂️', value: agg.total_calls || 0, unit: '', sub: 'Feb–Jun 2026', color: T.textHi },
    { label: 'Avg edge', icon: '⚖️', value: ((agg.avg_actual || 0) / Math.max(1, agg.avg_pred || 0)).toFixed(1), unit: '×', sub: 'actual / pred', color: T.textMid },
  ];

  const weekly = data.weekly || [];
  const selWeek = weekly.find((w) => w.week === selectedWeek) || weekly[weekly.length - 1];
  const weekPicks = (data.history || []).filter((p) => {
    if (!selWeek) return false;
    try {
      const d = new Date(p.date);
      const iso = d.getFullYear() + '-W' + String(Math.ceil(((d - new Date(d.getFullYear(), 0, 1)) / 86400000 + new Date(d.getFullYear(), 0, 1).getDay() + 1) / 7)).padStart(2, '0');
      return iso === selWeek.week;
    } catch { return false; }
  });

  return (
    <div style={{ animation: 'risein .35s ease' }}>
      {/* Title row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, letterSpacing: '-.02em' }}>🔥 Trending</h1>
            <span style={{
              padding: '4px 10px', borderRadius: 8,
              background: 'rgba(251,191,36,.12)', border: '1px solid rgba(251,191,36,.32)',
              color: T.gold, fontSize: 11, fontWeight: 800, letterSpacing: '.04em',
            }}>OPTIONS · 3× IN 7 DAYS</span>
          </div>
          <p style={{ margin: '8px 0 0', color: T.textMuted, fontSize: 13 }}>
            ML-ranked options setups · {data.latest_date} · {live.length} live picks
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.green }}>
            ↻ Updated {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : 'now'}
          </span>
          <button onClick={refresh} style={{
            padding: '9px 16px', borderRadius: 10, border: `1px solid ${T.border2}`,
            background: T.surface1, color: T.indigoLight, fontSize: 13, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 7,
          }}>
            <span style={{ display: 'inline-block', animation: spinning ? 'spin .9s linear' : 'none' }}>🔄</span>
            Refresh
          </button>
        </div>
      </div>

      {/* Card legend (click ? to learn what everything means) */}
      <CardLegend />

      {/* View switcher */}
      <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
        {[
          ['open', '📂 Open Trades', (data.open_trades || []).length],
          ['today', "🐲 Today's Picks", (data.live_picks || []).length],
          ['closed', '📚 Closed History', (data.closed_trades || []).length],
        ].map(([k, label, n]) => (
          <button key={k} onClick={() => setView(k)} style={{
            padding: '10px 18px', borderRadius: 12, fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            background: view === k ? 'linear-gradient(180deg,#221c11,#181208)' : T.surface1,
            border: `1px solid ${view === k ? T.borderWarm : T.border2}`,
            color: view === k ? T.gold : T.textMuted,
          }}>{label} <span style={{ fontFamily: T.mono, fontSize: 12, opacity: .8 }}>({n})</span></button>
        ))}
      </div>

      {view === 'closed' && (<>
      {/* Stat cards: hero + 6 supporting */}
      <div className="g-stat" style={{ display: 'grid', gridTemplateColumns: '340px 1fr', gap: 16 }}>
        <HeroStatCard
          value={(agg.hit_rate_15pct || 0).toFixed(1)}
          unit="%"
          label="15%+ HIT RATE"
          sub={`${agg.hits_15pct} / ${agg.total_calls} picks gained ≥15% in 10d`}
          edgeText={(() => {
            // Approx market base rate: random ticker hitting +15% in 10d ≈ 20% in bull regime.
            // Honest estimate: use weekly base derived from average actual.
            const base = 49.4; // computed once from the labeled test universe
            const edge = (agg.hit_rate_15pct || 0) - base;
            return `${edge >= 0 ? '+' : ''}${edge.toFixed(1)}pp vs market base rate`;
          })()}
          backtestText={`${weekly.length} weeks`}
          positiveText={(() => {
            const pos = weekly.filter((w) => (w.avg_call_actual || 0) > 0).length;
            return `${pos} / ${weekly.length}`;
          })()}
        />
        <div className="g-multi" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridTemplateRows: 'repeat(2, 1fr)', gap: 12 }}>
          {stats.map((s, i) => <StatCard key={i} {...s} />)}
        </div>
      </div>

      <TradingAccountPanel trading={trading} />
      </>)}

      {view === 'today' && (<>
      {/* Hero pick */}
      <HeroPick pick={heroPick} aggregate={agg} held={heldForHero} live={heroLiveData} livePut={livePutData} liveAsOf={heroAsOf} />
      <ConfidenceBar minCall={curF.mc} minPut={curF.mp} onCall={(v) => setF({ mc: v })} onPut={(v) => setF({ mp: v })} showCalls={curF.sc} showPuts={curF.sp} onToggleCalls={() => setF({ sc: !curF.sc })} onTogglePuts={() => setF({ sp: !curF.sp })} trades={todayF} />
      <div style={{ marginTop: 28 }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 700, color: T.gold, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>
          🐲 All of today's recommendations · {data.latest_date}
        </div>
        <div style={{ fontSize: 11.5, color: T.textMuted, marginBottom: 12 }}>Liquidity-gated fresh picks (option chains trade ≥100 contracts/day). Live P&L appears after each contract's first trade.</div>
        <TwoColTrades trades={todayF} onSelect={openTradeDock} volMap={volMap} pickRanks={pickRanks} onAnalyze={() => analyzePicks('today')} analyzing={pickBusy === 'today'} viewName="today" todayView />
      </div>
      </>)}

      {view === 'open' && (<>
      {bestOpen && <BestOpenCard pick={bestOpen} />}
      <ConfidenceBar minCall={curF.mc} minPut={curF.mp} onCall={(v) => setF({ mc: v })} onPut={(v) => setF({ mp: v })} showCalls={curF.sc} showPuts={curF.sp} onToggleCalls={() => setF({ sc: !curF.sc })} onTogglePuts={() => setF({ sp: !curF.sp })} trades={openF} />

      {/* Per-side live stats (update with the confidence slider) */}
      <div className="g-2" style={{ marginTop: 22, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <SideStatsPanel sideLabel="📈 Calls" accent={T.green} open={openCallsF} closed={closedCallsF} />
        <SideStatsPanel sideLabel="📉 Puts" accent={T.red} open={openPutsF} closed={closedPutsF} />
      </div>

      {/* Open trades */}
      <div style={{ marginTop: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 700, color: T.green, letterSpacing: '.06em', textTransform: 'uppercase' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: T.green, boxShadow: `0 0 8px ${T.green}` }} />
            Open trades · {openF.length} · live option P&L since entry
          </div>
          <select value={openSort} onChange={(e) => setOpenSort(e.target.value)} style={{ background: T.surface1, color: T.textMid, border: `1px solid ${T.border2}`, borderRadius: 8, padding: '6px 10px', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer' }}>
            <option value="pnl">Sort: P&L (high → low)</option>
            <option value="pnl_asc">Sort: P&L (low → high)</option>
            <option value="date">Sort: Newest opened</option>
            <option value="pred">Sort: Predicted upside</option>
          </select>
        </div>
        <div style={{ fontSize: 11.5, color: T.textMuted, marginBottom: 12 }}>Bought the ATM option at each signal; exits at +100% target / −50% stop / 7-day time-stop. Pending trades (no close yet) sort last.</div>
        {openTrades.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: T.textMuted, background: T.surface1, borderRadius: 12, border: `1px solid ${T.border2}` }}>No open trades right now.</div>
        ) : (
          <TwoColTrades trades={openF} onSelect={openTradeDock} volMap={volMap} pickRanks={pickRanks} onAnalyze={() => analyzePicks('open')} analyzing={pickBusy === 'open'} viewName="open" />
        )}
      </div>

      </>)}

      {view === 'closed' && (<>
      {/* Closed history with entry-date cutoff */}
      {(() => {
        const all = data.closed_trades || recentlyClosed;
        const cutDays = closedCutoff === '7d' ? 7 : closedCutoff === '14d' ? 14 : closedCutoff === '30d' ? 30 : null;
        const cutStr = cutDays ? new Date(new Date(data.latest_date).getTime() - cutDays * 86400000).toISOString().slice(0, 10) : null;
        const shown = (cutStr ? all.filter((r) => r.date >= cutStr) : all).filter(confPass);
        const pnls = shown.filter((r) => r.opt_pnl_pct != null).map((r) => r.opt_pnl_pct);
        const wins = pnls.filter((p) => p > 0).length;
        const avg = pnls.length ? pnls.reduce((a, b) => a + b, 0) / pnls.length : 0;
        return (
          <div style={{ marginTop: 32 }}>
            <ConfidenceBar minCall={curF.mc} minPut={curF.mp} onCall={(v) => setF({ mc: v })} onPut={(v) => setF({ mp: v })} showCalls={curF.sc} showPuts={curF.sp} onToggleCalls={() => setF({ sc: !curF.sc })} onTogglePuts={() => setF({ sp: !curF.sp })} trades={shown} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 4 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 700, color: T.textMid2, letterSpacing: '.06em', textTransform: 'uppercase' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: T.textMuted }} />
                Closed trades · realized P&L · {shown.length} trades{pnls.length ? ` · ${wins}/${pnls.length} wins · avg ${avg >= 0 ? '+' : ''}${avg.toFixed(0)}%` : ''}
              </div>
              <select value={closedCutoff} onChange={(e) => setClosedCutoff(e.target.value)} style={{ background: T.surface1, color: T.textMid, border: `1px solid ${T.border2}`, borderRadius: 8, padding: '6px 10px', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer' }}>
                <option value="all">Entered: all time</option>
                <option value="7d">Entered: last 7 days</option>
                <option value="14d">Entered: last 14 days</option>
                <option value="30d">Entered: last 30 days</option>
              </select>
            </div>
            <div style={{ fontSize: 11.5, color: T.textMuted, marginBottom: 12 }}>Exited via target / stop / 7-day time-stop. Click any card for full detail.</div>
            {shown.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: T.textMuted, background: T.surface1, borderRadius: 12, border: `1px solid ${T.border2}` }}>No closed trades in this window.</div>
            ) : (
              <TwoColTrades trades={shown} onSelect={openTradeDock} volMap={volMap} />
            )}
          </div>
        );
      })()}

      {false && recentlyClosed.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11, fontWeight: 700, color: T.textMid2, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 4 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: T.textMuted }} />
            Recently closed · realized P&L · last {openBook.window_days || 14} days
          </div>
          <div style={{ fontSize: 11.5, color: T.textMuted, marginBottom: 12 }}>Hit their target, stop, or 7-day time-stop. These fold into the all-time account after the window.</div>
          <div className="g-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {recentlyClosed.map((p, i) => (
              <PickCard key={'rc' + p.ticker + p.date + p.side + i} pick={p} rank={i + 1} onOpen={(panel) => openTradeDock(p, panel)} />
            ))}
          </div>
        </div>
      )}


      {/* Week picker */}
      <WeekPicker weekly={weekly} selected={selectedWeek} onSelect={setSelectedWeek} />

      {/* Week detail */}
      <WeekDetail week={selWeek} side={weekSide} onSideChange={setWeekSide} sort={weekSort} onSortChange={setWeekSort} />

      {/* Week outcome table */}
      <WeekOutcomeTable picks={weekPicks} side={weekSide} sort={weekSort} />

      </>)}

      {pickModal && <PicksAnalysisModal data={pickModal} loading={pickBusy === pickModal.view} onRerun={() => analyzePicks(pickModal.view, true)} onClose={() => setPickModal(null)} onOpen={(p) => { setPickModal(null); openTradeDock(p, 'details'); }} trades={pickModal.view === 'open' ? (data && data.open_trades || []) : (data && data.live_picks || [])} />}
      <TradeDock pick={dockPick} panels={dockPanels} pickRanks={pickRanks}
        onClose={(k) => setDockPanels((p) => ({ ...p, [k]: false }))}
        onCloseAll={() => { setDockPanels({ chart: false, news: false, details: false }); setDockPick(null); }} />

      {/* keyframes inline */}
      <style>{`
        @keyframes hglow {
          0%, 100% { box-shadow: 0 0 0 1px rgba(251,191,36,.4), 0 0 30px -6px rgba(251,191,36,.28); }
          50%      { box-shadow: 0 0 0 1px rgba(251,191,36,.55), 0 0 36px -4px rgba(251,191,36,.38); }
        }
        @keyframes risein { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 760px) {
          .g-2 { grid-template-columns: 1fr !important; }
          .g-multi { grid-template-columns: repeat(2, 1fr) !important; }
          .g-stat { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 460px) {
          .g-multi { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </div>
  );
}
